// src/lib/i18n/messages/en.ts
// English. Same keys as fr.ts (enforced by the type), same variables (enforced by the tests).
// Written 11/10/2026; to be reread by a native reader before the routing is switched on.

import type { Catalogue } from '../t';

export const en: Catalogue = {
  commun: {
    accueil: 'Home',
    acteurs: 'People',
    brouillon: 'Draft',
    surDix: '{note}/10',
    citation: '“{texte}”',
    laSource: 'source',
    ouvrirSource: 'Open the source',
    sansAdresse: 'no link',
    nonEvalueCourt: 'n/a',
    essentiel: 'The gist in 30 seconds',
    portraitDe: 'Portrait of {nom}',
    photo: 'Photo',
  },

  ia: {
    long: 'Lumière Noire’s analyses are produced with artificial intelligence models applying the same protocol to every subject, then reviewed and approved by a human before publication. They rely on cited public sources. They may contain errors or inaccuracies: they are an analysis, not a judicial finding or an established fact. Anyone concerned may request a correction or exercise a right of reply.',
    court: 'Analysis produced with AI assistance and reviewed by the editors.',
    methodologie: 'Methodology',
    signaler: 'Report an error / right of reply',
  },

  traduction: {
    auto: 'Machine-translated from French.',
    original: 'Read the original',
    aRelire: 'Translation awaiting review: visible to admins only until published.',
    perimee: 'Outdated translation: the French has changed since. Visible to admins only.',
    absente: 'This content has not been translated yet: it is shown in its original language.',
    section: 'This section is only available in French for now.',
  },

  pdf: {
    reserveAbonnes: 'PDF export is for subscribers',
    abonnes: 'PDF · subscribers',
    telecharger: 'Download this analysis as a PDF, in your name',
    preparation: 'Preparing…',
    exporter: 'Export as PDF',

    doc: {
      sommaire: 'Contents',
      sources: 'Sources',
      source: 'Source',
      sourceSansTitre: 'Untitled source',
      version: 'version {n}',
      brouillon: 'draft',
      nonPublie: 'unpublished',
      filigrane: 'Exported by {nom} on {date} · Lumière Noire',
      unAbonne: 'a subscriber',
      retenir: 'Key takeaways',
      dansCeDocument: 'In this document',
      note: 'Brief',
      pied: {
        avecVersion: '{genre} of {date}, v{version}',
        sansVersion: '{genre} of {date}',
        sansDate: '{genre} v{version}',
      },
      genres: {
        analyse: 'Analysis',
        barometre: 'Barometer',
        rayonx: 'RayonX',
        organisation: 'Profile',
        acteur: 'Profile',
      },
      produit: {
        analyse: 'Analysis produced on {date}',
        barometre: 'Barometer produced on {date}',
        rayonx: 'RayonX produced on {date}',
        organisation: 'Profile produced on {date}',
        acteur: 'Profile updated on {date}',
      },
      sansDate: {
        analyse: 'Analysis',
        barometre: 'Barometer',
        rayonx: 'RayonX',
        organisation: 'Organisation profile',
        acteur: 'Person profile',
      },
      couvertJusquau: 'events covered up to {date}',
      confiance: {
        high: 'high confidence',
        medium: 'medium confidence',
        low: 'low confidence',
      },
    },

    analyse: {
      kicker: 'Analysis',
      contexte: 'Background',
      perspective: 'Perspective: {label}',
      sansTitre: 'untitled',
      pointsCles: 'Key points',
      appuie: 'What supports it',
      contredit: 'What contradicts it',
      limites: 'Its limits',
      faits: 'Established facts',
      contestePar: 'Disputed by',
      chronologie: 'Timeline',
      comparaisons: 'Historical comparisons',
      ressemblances: 'Similarities',
      differences: 'Differences',
      lecons: 'Lessons',
      scenarios: 'Scenarios',
      probabilite: 'probability {n}%',
      probabiliteSuivie: 'probability {n}% (at the time of the analysis: {initiale}%, reviewed {date})',
      declencheurs: 'Triggers',
      consequences: 'Consequences',
      impact: 'Impact on citizens',
      resonance: 'Social resonance',
      sentiments: 'Dominant sentiments',
      recitsEmergents: 'Emerging accounts',
      manipulation: 'Signs of manipulation',
    },

    barometre: {
      conclusions: 'Key findings',
      pointsAttention: 'Points of concern',
      justification: 'Basis for the assessment',
      schema: 'Recurring pattern',
      facteurs: 'Context',
      engagements: 'Commitments — {domaine}',
      execution: 'delivery {note}/10',
      franchise: 'candour {note}/10',
      declaration: 'Statement — nothing to carry out',
      nonEvaluable: 'Not assessable (too vague to be measured)',
      passe: 'What happened',
      neDisaitPas: 'What the promise left out',
      noteFranchise: 'Candour score',
      nonTenus: 'Not kept',
      tenus: 'Kept',
      partiels: 'Partly kept',
      declarations: 'Statements',
      nonTenusSur: { one: '{n} commitment not kept out of {total}', other: '{n} commitments not kept out of {total}' },
      trahisSur: { one: '{n} commitment betrayed out of {total}', other: '{n} commitments betrayed out of {total}' },
      aucunEvaluable: 'No assessable commitment',
      executionLibelle: 'Delivery',
      franchiseLibelle: 'Candour',
      etatDeclaration: 'Statement',
      etatNonEvaluable: 'Not assessable',
      declarationPrecision: 'Nothing to carry out: only candour is scored.',
      nonEvaluablePrecision: 'Too vague to be measured.',
      methode: 'Two scores, kept apart. Delivery measures what was done; candour, what was said about the costs and the trade-offs. A statement only has the second.',
    },

    rayonx: {
      affirmation: 'The claim',
      verdict: 'The verdict',
      vrai: 'What is true',
      faux: 'What is false or missing',
      sousAffirmations: 'The sub-claims',
      pointsCles: 'Key points',
      confirme: 'What confirms',
      refute: 'What refutes',
      nuance: 'What qualifies',
      reserves: 'Caveats',
      preuves: 'The evidence, item by item',
      preuve: 'Evidence {n}',
      score: 'Verdict score',
      pourOui: 'Evidence for yes',
      pourNon: 'Evidence for no',
      confirment: 'Evidence that confirms',
      refutent: 'Evidence that refutes',
      nuancent: 'Evidence that qualifies',
      sansVerdict: 'No verdict',
      polarite: {
        confirms: 'confirms',
        refutes: 'refutes',
        nuance: 'qualifies',
      },
      poids: {
        p1: 'low weight',
        p2: 'moderate weight',
        p3: 'high weight',
      },
    },

    organisation: {
      kicker: 'Organisation',
      essentiel: 'In brief',
      quoi: 'What is it?',
      fondee: 'founded in {date}',
      siege: 'headquarters: {lieu}',
      budget: 'budget: {montant}',
      ditDElle: 'What it says about itself',
      poursuit: 'What it pursues',
      origine: 'Where it comes from',
      evolution: 'How it has evolved',
      pays: 'Countries of operation',
      qui: 'Who belongs to it?',
      categories: {
        politicalLeaders: 'Political leaders',
        corporateLeaders: 'Business leaders',
        financialSector: 'Finance',
        mediaProfessionals: 'Media',
        academicIntellectuals: 'Academics and intellectuals',
        internationalOrganizations: 'International organisations',
        militaryDefense: 'Defence',
      },
      invites: {
        strategicInvitees: 'Strategic guests',
        cooptedOpponents: 'Co-opted opponents',
        emergingLeaders: 'Emerging leaders',
      },
      invite: 'invited {annee}',
      pese: 'How does it weigh on decisions?',
      recos: 'Recommendations acted upon',
      crises: 'Crises seen coming',
      evoquee: 'raised: {date}',
      survenue: 'occurred: {date}',
      rdvDecisions: 'Gatherings and decisions',
      rdv: 'The gatherings',
      argent: 'Where does the money come from?',
      quiFinance: 'Who funds it',
      circuit: 'A circular funding loop',
      conflits: 'Conflicts of interest',
      transparence: 'Transparency',
      scoreTransparence: 'Transparency score: {n}/100.',
      travaille: 'Who does it work with?',
      orgsLiees: 'Linked organisations',
      membresPartages: '({n} shared members)',
      casquettes: 'Members wearing several hats',
      reseau: 'The network',
      retenir: 'What should we take away?',
      verdict: 'Verdict',
      constats: 'Key findings',
      probleme: 'What is problematic',
      legitimes: 'Legitimate contributions',
      ouvert: 'What remains open?',
      fiabilite: 'Reliability of the sources',
      pasEtabli: 'What the profile could not establish',
      pistes: 'Leads to follow',
      membresDocumentes: 'Documented members',
      rendezVous: 'Gatherings',
    },

    acteur: {
      kicker: 'Person profile',
      biographie: 'Biography',
      parolesActes: 'Words and deeds',
      barometreVersion: 'Barometer, version {n}',
      noteFranchise: 'candour score {note}/10',
      conclusions: 'Key findings',
      barometreComplet: 'The full barometer: {url}',
      investigation: 'Investigation',
      investigationComplete: 'The full investigation: {url}',
      rayonx: 'RayonX',
      positions: 'Public positions',
      position: 'Position',
      appartenances: 'Memberships',
      relations: 'Relationships',
      evenements: 'Events',
      analyses: 'In the analyses',
      organisations: 'Organisations',
      noteDeFranchise: 'Candour score',
      essentiel: 'Words and deeds: {accroche}.',
      roles: {
        speaker: 'speaker',
        attendee: 'attendee',
        organizer: 'organiser',
        moderator: 'moderator',
        observer: 'observer',
      },
    },
  },

  bs: {
    famille: 'Words and deeds',

    profils: {
      libelles: {
        "techno-illusionniste": "Heavy technical complexity",
        "promesses-saturateur": "Inflation of commitments",
        "chaos-assume": "Inconsistent positions",
        "economie-fiction": "Optimistic budget projections",
        "transferts-masques": "Unstated burden shifting",
        "techno-pragmatique": "Variable pragmatism",
        "hypocrite-moral": "Gap between words and conduct",
        "merite-emprunte": "Outside causes not acknowledged",
        "statisticien-creatif": "Indicator redefined along the way",
        "perimetre-glissant": "Scope narrowed along the way",
        "aucun": "No recurring pattern identified"
      },
      descriptions: {
        "techno-illusionniste": "Marked reliance on technical arguments, at the expense of making contradictions of substance legible.",
        "promesses-saturateur": "A multiplication of commitments that makes delivery hard to track.",
        "chaos-assume": "Successive or simultaneous contradictory positions that make assessment difficult.",
        "economie-fiction": "Poorly supported budget projections presented as realistic.",
        "transferts-masques": "Tax or social burdens shifted without explicit communication.",
        "techno-pragmatique": "Commitments revised under institutional or political constraint.",
        "hypocrite-moral": "A documented gap between stated values and observed conduct.",
        "merite-emprunte": "Favourable results presented as the product of the action, without mentioning the factors that contributed to them.",
        "statisticien-creatif": "A target presented as met after the measuring rule was changed.",
        "perimetre-glissant": "A target presented as met after what it covered was narrowed.",
        "aucun": "No systemic pattern identified in the commitments analysed."
      },
    },

    regle: {
      noteGlobale: 'overall score {note}',
      contredit: 'contradicted by actions',
      tenu: 'kept',
    },

    echelle: {
      nonEvalue: 'Not assessed',
      majeurs: 'Major documented gaps',
      significatifs: 'Significant gaps observed',
      partielle: 'Partly consistent',
      solide: 'Solidly consistent',
      elevee: 'Highly consistent',
    },

    page: {
      introuvable: 'Analysis not found',
      voirToutes: '← See all analyses',
      angleCible: 'Focus · {angle}',
      engagementsDe: 'Commitments of {nom}',
      nbEngagements: { one: '{n} commitment', other: '{n} commitments' },
      verdictEnCinq: 'The verdict in 5 minutes ↓',
      unParUn: { one: 'The commitment, in detail ↓', other: 'The {n} commitments, one by one ↓' },
      pied: 'Lumière Noire · Consistency analysis · {nom}',
    },

    onglets: {
      verdict: 'Verdict',
      engagements: 'Commitments',
    },

    verdict: {
      repartition: 'Score breakdown',
      quantifiables: 'Measurable commitments',
      qualitatifs: 'Qualitative commitments',
      nonEvaluables: {
        one: '{n} commitment could not be assessed (wording too vague)',
        other: '{n} commitments could not be assessed (wording too vague)',
      },
      conclusions: 'Key findings',
      pointsAttention: 'Points of concern',
      justification: 'Basis for the assessment',
      schemaRecurrent: 'Recurring pattern — detailed analysis',
      facteurs: 'Context',
    },

    liste: {
      avertissement: 'A representative selection of public commitments. Not exhaustive. Each assessment rests on documented, sourced facts.',
      moyenne: 'avg. {note}',
      titreNonEvaluable: 'Commitment too vague to be measured: not assessable',
      titreDeclaration: 'Statement: nothing to carry out, the score is the candour score',
      badgeDeclaration: 'statement · candour',
    },

    domaines: {
      economie_emploi: 'Economy',
      social_sante: 'Social / Health',
      securite_immigration: 'Security',
      environnement_energie: 'Environment',
      international_defense: 'International',
      institutions_democratie: 'Institutions',
    },

    classification: {
      quantifiable: 'measurable',
      qualitative: 'qualitative',
      floue: 'vague',
    },

    tenue: {
      tenu: 'Kept',
      partiel: 'Partly kept',
      non_tenu: 'Not kept',
    },

    carte: {
      declarationRien: 'Statement · nothing to carry out',
      franchise: 'candour {note}/10',
      deuxAxes: 'delivery {tenue}/10 · candour {franchise}/10',
      dit: 'WHAT WAS SAID',
      passe: 'WHAT HAPPENED',
      neDisaitPas: 'WHAT THE PROMISE LEFT OUT',
      sourcesDisent: 'WHAT THE SOURCES SAY',
      lecture: 'OUR READING',
      passage: 'the passage',
      motPourMot: 'Each fact is found word for word on its page.',
      sources: { one: 'The source', other: 'The {n} sources' },
      analyse: 'Detailed analysis',
      poids: 'How it weighs in the score',
      primaireNonEtablie: 'Primary source not established: the figure or act is cited here only by the media, not by the body that produces it.',
      emetteur: 'Issued by: {emetteur}',
      sourceN: 'Source {n}',
      quiPaie: 'WHO REALLY PAYS',
      payeurs: 'Payers: {payeurs} · Beneficiaries: {beneficiaires}',
      ecartStat: 'STATISTICAL GAP · {gravite}',
      dissonance: 'DISSONANCE OBSERVED',
    },

    pastilles: {
      budget: 'Budget',
      couts: 'Costs',
      clarte: 'Clarity of definition',
      coherence: 'Consistency of statements',
      stabilite: 'Stability over time',
      masquer: 'Hide the reason',
      voir: 'See why',
      pourquoi: '{libelle}: {valeur} — why',
      valeurs: {
        realiste: 'realistic',
        optimiste: 'optimistic',
        fantaisiste: 'fanciful',
        impossible: 'impossible',
        assume: 'acknowledged',
        minimise: 'played down',
        deni: 'denied',
        fiction: 'fiction',
        oui: 'yes',
        floue: 'vague',
        inexistante: 'none',
        coherent: 'consistent',
        contradictoire: 'contradictory',
        opportuniste: 'opportunistic',
        stable: 'stable',
        evolutive: 'shifting',
        reniee: 'disowned',
      },
    },

    notation: {
      deuxMesures: 'Two measures, and they differ',
      tenueTermes: 'Kept on its own terms',
      sinceriteReserves: 'Sincerity, once caveats are counted',
      retientSeconde: 'The barometer keeps the second: hitting a target by shifting the problem elsewhere, or by changing how it is counted, is not keeping it.',
      reservesDocumentees: { one: '{n} documented caveat', other: '{n} documented caveats' },
      poidsTitre: 'Weight of this commitment in the score · ×{total}',
      pouvoir: 'Power held — {valeur}',
      verifiabilite: 'Verifiability — {valeur}',
      chiffre: 'quantified commitment',
      appreciable: 'judged on substance',
      centralite: 'Centrality — {valeur}',
      nonRetenue: ' · not credited: {motif}',
      reserves: {
        transfert_de_charge: 'burden shifted',
        definition_indicateur: 'indicator redefined',
        perimetre_restreint: 'scope narrowed',
        causalite_non_reconnue: 'causes not acknowledged',
        financement_non_assume: 'funding not owned up to',
      },
      pouvoirs: {
        fonction_et_moyens: 'held office and the means',
        fonction_sans_moyens: 'held office without the means',
        candidat_elu: 'candidate, later elected',
        candidat_battu: 'candidate, not elected',
        aucun_levier: 'no lever on the issue',
      },
      centralites: {
        programme_repete: 'manifesto pledge, repeated',
        engagement_assume: 'public commitment, owned',
        propos_de_circonstance: 'passing remark',
      },
      motifs: {
        moins_de_deux_occurrences_datees: 'fewer than two dated occurrences',
        aucune_occurrence_sourcee: 'no occurrence backed by a link',
      },
    },

    profil: {
      phrasesReserve: {
        transfert_de_charge: 'a target met by shifting the burden onto someone else',
        financement_non_assume: 'spending announced without saying who pays for it',
        definition_indicateur: 'a target met after the measuring rule was changed',
        perimetre_restreint: 'a target met after what it covered was narrowed',
        causalite_non_reconnue: 'favourable results presented as the product of the action, without mentioning what contributed to them',
      },
      aucuneReserve: {
        one: 'Of {n} assessable commitment, no documented caveat: the gaps found are outright contradictions, with no burden shifting, redefinition or narrowed scope.',
        other: 'Of {n} assessable commitments, no documented caveat: the gaps found are outright contradictions, with no burden shifting, redefinition or narrowed scope.',
      },
      avecReserve: {
        one: 'Of the {total} commitments, {n} comes with a caveat.',
        other: 'Of the {total} commitments, {n} come with a caveat.',
      },
      laPlusFrequente: 'The most frequent, {fois}: {phrase}.',
      ensuite: 'Next, {fois}, {phrase}.',
      fois: { one: 'once', other: '{n} times' },
    },
  },

  nav: {
    brief: 'The Brief',
    analyses: 'Analyses',
    rayonx: 'RayonX',
    acteurs: 'People',
    organisations: 'Organisations',
    enquetes: 'Investigations',
    monQg: 'My HQ',
    evitement: 'Skip to main content',
    premium: 'For Premium subscribers',
    rechercherRaccourci: 'Search (Ctrl+K)',
    rechercherPoints: 'Search…',
    deconnexion: 'Log out',
    connexion: 'Log in',
    fermerMenu: 'Close the menu',
    menu: 'Menu',
    modeClair: 'Switch to light mode',
    modeSombre: 'Switch to dark mode',
  },

  pied: {
    explorer: 'Explore',
    liensLegaux: 'Legal links',
    actu: 'News',
    graphe: 'The Graph',
    about: 'About',
    methodologie: 'Methodology',
    mentions: 'Legal notice',
    confidentialite: 'Privacy',
    corrections: 'Corrections & right of reply',
    credits: 'Photo credits',
  },

  recherche: {
    titre: 'Search',
    champ: 'A person, an organisation, a topic…',
    fermer: 'Close',
    echap: 'Esc',
    resultats: 'Results',
    allerA: 'Go straight to',
    rien: 'Nothing for “{requete}”.',
    conseil: 'Check the spelling, or try a surname on its own.',
    nbResultats: { one: '{n} result', other: '{n} results' },
    touches: '↑ ↓ to choose · Enter to open',
    types: {
      actor: 'People',
      organization: 'Organisations',
      analysis: 'Analyses',
      investigation: 'Investigations',
    },
    raccourcis: {
      brief: 'Today’s Brief',
      analyses: 'Analyses',
      acteurs: 'People',
      organisations: 'Organisations',
      rayonx: 'RayonX checks',
      enquetes: 'Investigations',
    },
  },

  ui: {
    lireLaSuite: 'Read more →',
    reduire: '↑ Show less',
  },

  suite: {
    titre: 'What next?',
    sousTitre: 'Follow the thread into other investigations.',
    familles: {
      barometre: 'Words and deeds',
      investigation: 'Investigation',
      organisation: 'Network',
      anatomie: 'Anatomy',
      analyse: 'Analysis',
      rayonx: 'RayonX',
    },
  },

  parcours: {
    baroTitre: '{nom}: words and deeds',
    baroLien: 'Their commitments set against what was done',
    invTitre: 'Investigation: {nom}',
    invLien: 'Career, networks, grey areas',
    membre: '{nom} is a member',
    citeeIci: 'Cited on this page',
    figure: '{nom} appears in it',
    acteurDansAnalyse: '{nom} is one of its actors',
    affirmation: 'A claim about {nom}, checked',
    orgDansAnalyse: '{nom} is cited in it',
  },

  accueil: {
    titreDebut: 'Understand, so as not to',
    titreFin: 'be swept along.',
    chapeau: 'Between news channels on a loop and websites that see conspiracies everywhere, a rigorous space was missing. Sourced facts, documented contradictions, mapped networks. Your conclusions.',
    lireBrief: 'Read today’s Brief →',
    parOuCommencer: 'Where to start?',
    aLaUne: 'Top stories',
    aVousDeJuger: 'You be the judge',
    vedette: 'Today’s spotlight',
    tousActeurs: 'All people →',
    tousDossiers: 'All files →',
    trackRecord: 'Full track record →',
    voirGraphe: 'See the graph →',
    toutesOrgs: 'All organisations →',
    precedent: 'Previous',
    suivant: 'Next',
    voirElement: 'See {nom}',
    aLirePlusTard: 'Read later',
    enregistreCourt: 'saved for later',
    enregistreLong: 'what you saved to dig into on a big screen',
    reprendre: 'Resume →',
    retirer: 'Remove',
    bilan: {
      confirmed: { one: 'confirmed', other: 'confirmed' },
      partially_confirmed: { one: 'partly', other: 'partly' },
      invalidated: { one: 'invalidated', other: 'invalidated' },
      pending: { one: 'pending', other: 'pending' },
      sansTri: { one: 'Our full record, unfiltered — {n} invalidated scenario included.', other: 'Our full record, unfiltered — {n} invalidated scenarios included.' },
    },
    conversion: {
      fermer: 'Close',
      badge: 'Free account · 30 seconds',
      accrocheDebut: 'Lumière Noire goes further',
      accrocheFin: 'when it knows you.',
      sauvegarder: 'Save analyses and profiles in My HQ',
      soumettre: 'Suggest topics, vote on suggestions',
      creer: 'Create a free account →',
      dejaCompte: 'Already have an account? Log in',
    },
  },

  types: {
    acteurs: {
      head_of_state: 'Head of state',
      minister: 'Minister',
      politician: 'Political figure',
      party_leader: 'Party leader',
      diplomat: 'Diplomat',
      corporate_executive: 'Business executive',
      businessman: 'Entrepreneur',
      banker: 'Banker / Financier',
      lobbyist: 'Lobbyist',
      military: 'Military',
      intelligence: 'Intelligence',
      magistrate: 'Judge / Prosecutor',
      journalist: 'Journalist',
      media_owner: 'Media owner',
      academic: 'Academic / Expert',
      ngo_leader: 'NGO leader',
      union_leader: 'Trade unionist',
      activist: 'Activist / Civil society',
      religious_leader: 'Religious leader',
      oligarch: 'Oligarch',
      business: 'Entrepreneur',
      company: 'Company',
      institution: 'Institution',
      media: 'Media',
      lobby: 'Lobby',
      ngo: 'NGO leader',
    },
    organisations: {
      think_tank: 'Think tank',
      forum: 'Forum',
      club: 'Club / Circle',
      lobby: 'Lobby / Pressure group',
      foundation: 'Foundation',
      ngo: 'NGO',
      union: 'Trade union',
      political_party: 'Political party',
      religious: 'Religious organisation',
      corporation: 'Private company',
      state_company: 'State-owned company',
      bank: 'Bank / Financial institution',
      media: 'Media group',
      intergovernmental: 'Intergovernmental organisation',
      military: 'Military organisation / Alliance',
      intelligence: 'Intelligence service',
      university: 'University / Academic institution',
      unknown: 'Unclassified',
    },
  },

  verdicts: {
    libelles: {
      vrai: 'True',
      'plutôt_vrai': 'Mostly true',
      partiellement_vrai: 'Partly true',
      'plutôt_faux': 'Mostly false',
      faux: 'False',
      'non_vérifiable': 'Unverifiable',
    },
    reponses: {
      vrai: 'Yes',
      'plutôt_vrai': 'Probably yes',
      partiellement_vrai: 'Partly',
      'plutôt_faux': 'Probably not',
      faux: 'No',
      'non_vérifiable': 'Impossible to say',
    },
    issues: {
      confirmed: 'Confirmed',
      partially_confirmed: 'Partly confirmed',
      invalidated: 'Invalidated',
      pending: 'In motion',
    },
  },

  familles: {
    analyse: 'Analysis',
    barometre: 'Consistency barometer',
    parolesEtActes: 'Words and deeds',
    rayonx: 'RayonX',
    anatomie: 'Anatomy',
    loupe: 'Magnifier',
    investigation: 'Investigation',
    organisation: 'Organisation',
    dossier: 'File',
    acteur: 'Person',
    thematique: 'Theme',
    scenario: 'Scenario',
    declaration: 'Statement',
  },

  home: {
    dernieresDepeches: 'Latest dispatches',
    leBrief: 'The Brief →',
    touteActu: 'All the news →',
    critique: 'Critical',
    comprendre: 'Understand: {famille} {titre} →',
    depuisVisite: 'Since your last visit',
    nbDepeches: { one: '{n} dispatch →', other: '{n} dispatches →' },
    nbPublications: { one: '{n} publication', other: '{n} publications' },
    publicationsDepuis: { one: '{n} publication since {date}', other: '{n} publications since {date}' },
    vraiOuFaux: 'True or false?',
    votreAvis: 'What do you think?',
    aVotreAvis: 'What do you think?',
    bienVu: 'Well spotted.',
    pasCetteFois: 'Not this time.',
    voirVerification: 'See the check and its sources →',
    declarationKicker: 'Words and deeds · Statement',
    coherentsQuestion: 'Words and deeds: consistent?',
    voirSesEngagements: 'See their {n} commitments set against their actions →',
    reponses: {
      vrai: 'True',
      nuance: 'Mixed',
      faux: 'False',
      coherents: 'Consistent',
      en_partie: 'Partly',
      contredits: 'Contradicted',
    },
    engagement: {
      coherents: 'Words and deeds consistent',
      en_partie: 'Partly consistent',
      contredits: 'Words contradicted by actions',
    },
    notesGlobales: 'The barometers’ overall scores',
    questions: {
      promesses: {
        titre: 'Who keeps their promises?',
        texte: 'Public figures’ commitments, set against what they actually did.',
      },
      verifications: {
        titre: 'Is it true that…?',
        texte: 'Claims doing the rounds, sifted through the sources.',
      },
      reseaux: {
        titre: 'Who mixes with whom?',
        texte: 'What these circles are, how far their influence reaches, and who sits in them.',
      },
      avenir: {
        titre: 'How to make sense of it?',
        texte: 'Each topic read from several angles, sources in hand, through to where it might lead.',
      },
      enquetes: {
        titre: 'Let’s dig deeper',
        texte: 'Systems, affairs, careers: examined in detail, sources in hand.',
      },
    },
    pasIci: 'What you won’t find here: certainties dressed up as facts, or conclusions drawn beyond what the evidence allows.',
    notreMethode: 'Read our method →',
    trackRecord: 'Full track record →',
    aBouge: 'What has moved',
    scenariosRevusRecemment: 'Recently revised scenarios',
    scenariosRevus: 'revised scenarios',
    revusFaits: 'Our scenarios, revised in the light of the facts',
    enHausse: 'rising',
    enBaisse: 'falling',
    tuiles: {
      nbEngagementsActes: 'The {n} commitments set against the record →',
      lireBarometre: 'Read the barometer →',
      leBarometre: 'The barometer',
      dossierKicker: 'File · {ancre}',
      metaAnalyse: ' · meta-analysis',
      leDossier: 'The file',
      scenarioKicker: 'Scenario · {sujet}',
      analyseMin: 'analysis',
      lAnalyse: 'The analysis',
      lAnalyseFleche: 'The analysis →',
      siegePartout: 'Sits everywhere',
      organisations: 'organisations',
      leProfil: 'The profile',
      membresCommun: 'Members in common',
      membresCommunsEntre: '{n} members shared by {a} and {b}',
      auMoins: 'At least {n} people sit on both',
      dont: ', including {noms}',
      deConsensus: 'consensus',
      consensusLectures: 'Consensus across readings',
      laFiche: 'The profile',
      lEnquete: 'The investigation',
      toucherReveler: 'Tap to reveal the verdict',
      verdictPreuves: 'The verdict and its evidence →',
    },
  },

  decouverte: {
    promesses: '{p} public figures, {e} commitments set against the record',
    verifications: { one: '{n} claim checked, with sources', other: '{n} claims checked, with sources' },
    reseaux: '{n} organisations analysed',
    reseauxCumul: ', {n} people in several of them',
    membresDocumentes: '{nom} · {n} documented members',
    sujets: '{n} topics analysed',
    scenariosDates: ', {n} dated scenarios',
    anatomies: { one: '{n} system anatomy', other: '{n} system anatomies' },
    loupes: { one: '{n} close-up on affairs', other: '{n} close-ups on affairs' },
    investigations: { one: '{n} investigation', other: '{n} investigations' },
    enquetesFond: 'in-depth investigations published',
    titreAvenir: '{noms}… how to make sense of it?',
    titreReseaux: '{noms}… what are they, and who’s in them?',
  },

  relie: {
    analysis: 'Analysis',
    investigation: 'Investigation',
    bs: 'Consistency',
    organisation: 'Mapping',
  },

  signes: {
    balance: {
      confirme: { one: '{n} confirms', other: '{n} confirm' },
      nuance: { one: '{n} qualifies', other: '{n} qualify' },
      refute: { one: '{n} refutes', other: '{n} refute' },
      pourOui: '{n} for yes',
      pourNon: '{n} for no',
      preuves: '{n} pieces of evidence: {detail}',
      voirOui: 'See the evidence pointing to yes',
      voirNon: 'See the evidence pointing to no',
      voirConfirment: 'See the evidence that confirms',
      voirNuancent: 'See the evidence that qualifies',
      voirRefutent: 'See the evidence that refutes',
    },
    calendrier: {
      scenariosDates: '{n} dated scenarios',
      point: '{titre}: {p}%, due {echeance}',
    },
    constellation: {
      aria: '{n} members by sector',
      dont: ', including {noms}',
    },
    pouls: {
      aria: '{total} dispatches over {semaines} weeks, {critiques} of them critical',
      semaine: { one: 'Week of {debut}: {n} dispatch', other: 'Week of {debut}: {n} dispatches' },
      dontCritiques: { one: ', {n} critical', other: ', {n} critical' },
      legende: 'One bar per week, by date of the event',
      critiques: 'of which critical',
    },
    maillage: {
      aria: { one: '{n} person sits on several organisations', other: '{n} people sit on several organisations' },
      barre: { one: '{n} person on {k} organisations', other: '{n} people on {k} organisations' },
      legende: { one: '{n} person on several organisations, by number of organisations', other: '{n} people on several organisations, by number of organisations' },
    },
    calendrierCouverture: { one: '{n} dated outlook ahead · higher means more likely', other: '{n} dated outlooks ahead · higher means more likely' },
    presence: {
      aria: 'Presence across the {total} sub-investigations',
      ligne: { one: '{nom}: {n} sub-investigation out of {total}', other: '{nom}: {n} sub-investigations out of {total}' },
      legende: 'presence across the {total} sub-investigations',
    },
    regle: 'Promise ruler',
  },

  temps: {
    aVenir: 'Upcoming',
    aujourdhui: 'Today',
    hier: 'Yesterday',
    ilYa: { one: '{n} day ago', other: '{n} days ago' },
  },

  importance: {
    critical: 'Critical',
    high: 'Important',
    medium: 'Worth noting',
    low: 'Minor',
  },

  brief: {
    metaTitre: 'The Brief',
    metaDescription: 'The geopolitical essentials in 3 minutes — this week’s dispatches, recent analyses, latest check.',
    kicker: 'The Brief · {date}',
    titre: 'Three minutes for the week',
    faitsARetenir: { one: 'fact to remember · with sources', other: 'facts to remember · with sources' },
    aRetenir: 'This week’s key facts',
    semaineCalme: 'A quiet week: no major fact linked to our analyses. The full feed is in',
    lActualite: 'the news',
    pourComprendre: 'To understand · {famille}:',
    sources: 'Sources',
    cestTout: 'That’s all for this week. Want to dig deeper?',
    touteActu: 'See all the news →',
  },

  portes: {
    publie: 'What we have published',
    nbPublications: { one: '{n} publication on {nom}', other: '{n} publications on {nom}' },
    baroDefaut: 'Commitments set against the record, one by one',
    leBarometre: 'The barometer',
    anglesReserves: { one: '{n} more angle, for subscribers', other: '{n} more angles, for subscribers' },
    voirAngles: 'See the angles',
    leDossierDe: 'The {nom} file',
    leDossier: 'The file',
    leVerdict: 'The verdict →',
  },

  listes: {
    plutotVrais: { one: '{n} mostly true', other: '{n} mostly true' },
    partiellementVrais: { one: '{n} partly true', other: '{n} partly true' },
    plutotFaux: { one: '{n} mostly false', other: '{n} mostly false' },
    verdictsPublies: 'Published verdicts: {legende}',
  },

  aria: {
    filAriane: 'Breadcrumb',
  },

  acteur: {
    positions: 'Positions in the analyses',
    voirPositions: { one: 'See {n} more position', other: 'See {n} more positions' },
    positionsPremium: 'Positions in the analyses are for Premium subscribers',
    apparaitDans: { one: 'Appears in {n} analysis', other: 'Appears in {n} analyses' },
    analyseN: 'Analysis #{n}',
    voirAnalyses: { one: 'See {n} more analysis', other: 'See {n} more analyses' },
    pasDansCorpus: 'This person does not yet appear in our analyses.',
    aucuneRelation: 'No documented relationship or participation for this person.',
    orgInconnue: 'Unknown organisation',
    inconnu: 'Unknown',
    nbRelations: { one: '{n} relationship', other: '{n} relationships' },
    nbOrganisations: { one: '{n} organisation', other: '{n} organisations' },
    nouveautesReseau: 'New in the network',
    detecteesRecemment: '· recently detected',
    aujourdhui: 'today',
    hier: 'yesterday',
    ilYaJ: '{n} d ago',
    participations: 'Event participation',
    voirOrganisations: { one: 'See {n} more organisation', other: 'See {n} more organisations' },
    nbEvents: { one: '{n} event', other: '{n} events' },
    roles: {
      speaker: 'Speaker',
      attendee: 'Attendee',
      organizer: 'Organiser',
      moderator: 'Moderator',
      observer: 'Observer',
    },
    relations: {
      inner_circle: 'Inner circle',
      political: 'Political',
      corporate: 'Business',
      media: 'Media',
      international: 'International',
      unexpected: 'Unexpected',
    },
    chargement: 'Loading…',
    introuvable: 'Person not found',
    retour: '← Back to people',
    acteur: 'Person',
    explorerReseau: 'Explore their network in the graph →',
    enBref: 'In brief',
    wikipedia: 'Wikipedia ↗',
    affiliations: 'Affiliations',
    frequente: 'Connected to these organisations',
    organisationN: 'Organisation #{n}',
    membres: {
      founder: 'Founder',
      core_member: 'Member',
      alumni: 'Alumni',
      young_leader: 'Young Leader',
      invitee: 'Guest',
      honorary: 'Honorary',
      emerging: 'Emerging',
    },
    onglets: {
      analyses: 'Analyses & investigations',
      reseau: 'Network',
      contexte: 'In our analyses',
      actualites: 'News',
    },
    analyserEnEntier: 'Should {nom} be analysed in full?',
    analyserSous: 'Put this analysis to the vote — the most requested topics get the full treatment: consistency of commitments, networks, sources.',
    enqueter: 'Should we investigate {nom}?',
    enqueterSous: 'Put this investigation to the vote — demand decides what we analyse next.',
  },

  enq3: {
    essentielSous: 'What the investigation establishes against received wisdom. Choose a fact: on the right, the associates it names.',
    constatMajeur: 'The most significant finding',
    apparaitDansN: { one: 'comes up in {n} other item of the investigation', other: 'comes up in {n} other items of the investigation' },
    nommeN: { one: 'names {n} associate', other: 'names {n} associates' },
    sertAussiN: { one: 'supports {n} item of the investigation', other: 'supports {n} items of the investigation' },
    depuis: 'since {annee}',
    ouApparait: 'Where they come up in the investigation',
    nulleParAilleurs: 'The investigation names them nowhere else.',
    prochesNommes: 'The associates it names',
    memeSource: 'The same source also supports',
    ouSert: 'What the investigation draws from it',
    sourceGenerale: 'No specific item cites this address: it documents the investigation as a whole.',
    groupes: {
      essentiel: 'Key points', parcours: 'Career', formation: 'Education', parcoursPrive: 'Private-sector career',
      affaires: 'Proceedings', enquetesEnCours: 'Ongoing inquiries', recoupement: 'Cross-checking, case by case',
      flux: 'Documented flows', conflits: 'Documented conflicts of interest', patrimoine: 'Assets', transactions: 'Notable transactions',
      construction: 'How it was built, period by period', trainDeVie: 'Lifestyle and property',
      premierCercle: 'Inner circle', politique: 'In politics', milieuAffaires: 'In business', presse: 'In the press', international: 'Internationally',
      cerclesInfluence: 'Circles of influence', liensInattendus: 'Unexpected ties', nominations: 'How they appoint', protection: 'Institutional protection', opposants: 'Opponents',
      mecanismes: 'The mechanisms', amplifie: 'What is amplified', disparait: 'What disappears', favorables: 'Media described as favourable', critiques: 'Media described as critical',
      contradictions: 'What was said, what was done', ordinaire: 'What departs from the ordinary', schemas: 'Recurring patterns', hypotheses: 'Competing hypotheses',
      centrale: 'The central question', questions: 'Other questions', rumeursEcartees: 'Rumours set aside for lack of corroboration', manque: 'What is missing to decide', biais: 'Possible biases of this investigation',
      sources: 'Sources',
    },
    etiquettes: {
      decisions: 'What the investigation notes', anomalies: 'What stands out', refutation: 'Rebuttals',
      declare: 'Declared', estime: 'Estimated', ecart: 'Gap', benefices: 'What each side gains', roleReel: 'Official role, real role',
      observe: 'Observed', attendu: 'Expected', intention: 'If it is intentional', coincidence: 'If it is coincidence', systeme: 'If it is the system',
      raison: 'Why it was set aside', biais: 'What shapes this source',
    },
  },
  veille3: {
    sommaire: 'Monitoring contents',
    surtitre: 'Monitoring',
    titre: 'What to read',
    fil: 'The whole feed',
    groupeMoment: 'By time',
    groupeNature: 'By kind',
    avenir: 'Coming up',
    semaine: 'This week',
    mois: 'This month',
    ancien: 'Older',
    major_evolution: 'Major developments',
    contradiction: 'Contradictions',
    scenario_update: 'Outlooks',
    new_relationship: 'New relationships',
    sujets: 'Subjects followed',
    unite: 'dispatches',
    uniteSujets: 'subjects',
    critiquesSeules: 'Critical only',
    nonDatees: 'No event date',
    nonDateesAide: 'Contradictions the monitoring found in older facts: they are not today’s news.',
    releveeLe: 'found on {date}',
    contexte: 'Context',
    quiBouge: 'What is moving most',
    quiBougeAide: 'The subjects with the most dispatches over thirty days.',
    nRecents: { one: '{n} dispatch in 30 days', other: '{n} dispatches in 30 days' },
    laSuite: 'More on this subject',
    seule: 'No other dispatch on this subject.',
    voirFiche: 'See the profile →',
    toutLeSujet: 'The whole subject in the centre →',
    sujetChoisi: 'The feed: {sujet}',
    retirerSujet: 'Back to the whole feed',
    choisir: 'Choose a dispatch to see its summary, its sources and what came next.',
    rien: 'Nothing here for these filters.',
    pivot: 'Switch menu',
    parSujet: 'By subject',
    nSujets: { one: '{n} subject', other: '{n} subjects' },
  },
  org3: {
    tous: 'All',
    legendeToile: 'The stronger the link (strength 1 to 10), the closer the organization and the thicker the line; dashed: strength not recorded',
    legendeMembres: 'Inner ring: current members · outer: former members, guests, and members with no dated period',
    essentielSous: 'What you need to know about the organization. On the right: the profile at a glance, and the people who come up most.',
    enBref: 'At a glance',
    fondee: 'Founded',
    siege: 'Headquarters',
    statut: 'Legal status',
    pays: { one: 'Active in {n} country', other: 'Active in {n} countries' },
    quiRevient: 'Who comes up most in the profile',
    nElements: { one: '{n} item', other: '{n} items' },
    apparaitDansN: { one: 'comes up in {n} other item of the profile', other: 'comes up in {n} other items of the profile' },
    nommeN: { one: 'names {n} member of the profile', other: 'names {n} members of the profile' },
    sertAussiN: { one: 'supports {n} item of the profile', other: 'supports {n} items of the profile' },
    ouApparait: 'Where they come up in the profile',
    nulleParAilleurs: 'The profile names them nowhere else.',
    nommes: 'Members it names',
    memeSource: 'The same source also supports',
    ouSert: 'What the profile draws from it',
    sourceGenerale: 'No specific item cites this address: it documents the profile as a whole.',
    roles: {
      fondateur: 'Founder', membre: 'Member', invitesStrategiques: 'Strategic invitee', opposantsCooptes: 'Co-opted opponent',
      leadersEmergents: 'Emerging leader', multiAffilie: 'Member shared with other organizations',
    },
    groupes: {
      identite: 'What it says it is, what it aims for', phases: 'The phases', fondation: 'The founding', fondateurs: 'The founders', structure: 'The structure',
      invitesStrategiques: 'Strategic invitees', opposantsCooptes: 'Co-opted opponents', leadersEmergents: 'Emerging leaders cultivated',
      recommandations: 'Recommendations followed', crises: 'Crises seen coming', canaux: 'Channels of influence', evenements: 'Gatherings',
      financements: 'Funding sources', circuit: 'The money circuit', conflits: 'Conflicts of interest', depenses: 'Where the money goes', manquements: 'Transparency failures',
      ecosysteme: 'The ecosystem', orgsLiees: 'Linked organizations', multiAffilies: 'Shared members', position: 'Its position in the network', coordination: 'How the network coordinates',
      verdict: 'The verdict', constats: 'Key findings', anomalies: 'Anomalies', problematiques: 'What is problematic', legitimes: 'Legitimate contributions',
      recommandationsFiche: 'What the profile recommends', effets: 'What it produces',
      limites: 'Data limitations', appui: 'How solid the profile is', pistes: 'Lines of inquiry', sources: 'Sources',
    },
    etiquettes: {
      realisations: 'Achievements', niveau: 'Level of influence', motivations: 'Motivations analyzed', connexions: 'Connections', trajectoire: 'Later career',
      nonDivulgues: 'Undisclosed topics', contributeurs: 'Main contributors', conditions: 'Conditions', etapes: 'The steps', roi: 'What it returns',
      preuves: 'Evidence', biais: 'What slants this source',
    },
  },
  bs3: {
    vue: 'Overview',
    vueTitre: '{nom}’s promises, set against the record',
    vueChapo: 'Consistency score: {note}/10 — {libelle}. Each finding opens its section; each promise carries a number, the same throughout the page.',
    groupeVerdict: 'The verdict',
    retenir: 'Key takeaways',
    attention: 'Points of concern',
    schema: 'The recurring pattern',
    note: 'Why this score',
    groupePromesses: 'The promises',
    toutes: 'All',
    declarations: 'Statements',
    groupeDepuis: 'Since',
    depuis: 'What they have said since',
    nonTenusSur: { one: '{n} not kept out of {total}', other: '{n} not kept out of {total}' },
    nTenus: { one: '{n} kept', other: '{n} kept' },
    nPartiels: { one: '{n} partly kept', other: '{n} partly kept' },
    nNonTenus: { one: '{n} not kept', other: '{n} not kept' },
    nDeclarations: { one: '{n} statement', other: '{n} statements' },
    nNonEvaluables: { one: '{n} not assessable', other: '{n} not assessable' },
    domaines: 'By area',
    moinsTenu: 'Least kept: {domaine}, {note}/10',
    mieuxTenu: 'Best kept: {domaine}, {note}/10',
    depuisReserve: 'Positions recorded since, promise by promise',
    nPositions: { one: '{n} position recorded since', other: '{n} positions recorded since' },
    nPositionsCourt: { one: '{n} position since', other: '{n} positions since' },
    depuisSous: { one: 'Linked to {n} promise', other: 'Linked to {n} promises' },
    depuisChapo: 'What our monitoring has recorded of their words, linked to the promise it touches. Solid line: a link; dashed: a connection.',
    depuisTitre: 'What they have said since',
    depuisReserveLong: 'Positions recorded since, linked to each promise, are reserved for subscribers.',
    enFrancais: 'Positions recorded in French.',
    rapprochement: 'connection',
    rapprochementAide: 'A connection: the position touches the subject of this promise without referring to it directly.',
    lienAide: 'The position bears directly on this promise.',
    laPromesseTouchee: 'The promise it touches',
    analyse: 'In the analysis “{titre}”',
    parDomaine: 'The promises, area by area',
    parDomaineAide: 'Each badge is a promise, with its delivery score out of 10 (dashed: a statement, scored on its candour). Click to open it.',
    commentTitre: 'How this record is built',
    selection: '{collectes} public commitments collected, {retenus} kept for this record.',
    ecartes: 'Left out of the selection',
    lesPires: 'Least kept',
    constatsAide: 'Choose a finding: on the right, the promises it refers to, each with its number.',
    promessesInvoquees: 'The promises it refers to',
    promessesInvoqueesSous: 'Click to open the promise in detail.',
    promessesNommeesSous: 'Recognised by the wording of the passage. Click to open the promise in detail.',
    aucuneInvoquee: 'This passage refers to no promise in particular.',
    nPromesses: { one: '{n} promise', other: '{n} promises' },
    tropVague: 'too vague to be measured',
    nReserves: { one: '{n} caveat', other: '{n} caveats' },
    promesseN: 'Promise {n}',
    ceQueDitLeVerdict: 'What the verdict says about it',
    precedente: 'Previous',
    suivante: 'Next',
  },
  plus3: {
    vue: 'Overview',
    vueChapo: 'What the analysis documents beyond the facts: where the matter comes from, where it may go, whom it affects, and what it rests on. Each finding opens its section.',
    groupeTemps: 'Time',
    groupeGens: 'People',
    groupeMethode: 'Method',
    precedents: 'Precedents',
    impact: 'Who is affected',
    reseaux: 'Social media',
    sources: 'Sources',
    chronoConstat: { one: '{n} event, from {debut} to {fin}', other: '{n} events, from {debut} to {fin}' },
    chronoChapo: 'In date order. Choose an event: on the right, what it changed and what its sources say.',
    peuConnus: { one: 'Including {n} little known.', other: 'Including {n} little known.' },
    precedentsConstat: { one: '{n} historical precedent', other: '{n} historical precedents' },
    precedentsChapo: 'What has happened before that comes close: what is alike, what differs, and what can be learned from it.',
    sourcesDuPrecedent: 'The sources this precedent cites. A marker in the text highlights its own.',
    scenarioHaut: 'Most likely: {titre} — {p}%',
    scenariosConstat: { one: '{n} estimated scenario', other: '{n} estimated scenarios' },
    scenariosChapo: { one: '{n} scenario. Choose a scenario: on the right, what would trigger it, what it would lead to, and whom it concerns.', other: '{n} scenarios, from most to least likely. They are not all mutually exclusive: their probabilities need not add up to 100. Choose a scenario: on the right, what would trigger it, what it would lead to, and whom it concerns.' },
    echeance: 'deadline {date}',
    declencheurs: 'What would trigger it',
    implications: 'What it would lead to',
    acteursConcernes: 'The actors it concerns',
    probaBouge: 'How its probability has moved',
    acteursConstat: { one: '{n} actor, {k} of them discreet', other: '{n} actors, {k} of them discreet' },
    plusGrandEcart: 'Widest gap: {nom}, {n} points more influence than visibility',
    nuageAria: 'Actors placed by visibility and influence',
    zoneDiscrete: 'Acts more than it shows',
    nuageAide: 'Above the diagonal: more influence than visibility. Points can be selected.',
    famille: { visible: 'Visible', discret: 'Discreet', victime: 'Victims' },
    familleTitre: { visible: 'The visible, by influence', discret: 'The discreet, by gap', victime: 'The victims' },
    tous: 'All',
    jaugesAide: 'On each line: influence (dark bar), then visibility (purple bar).',
    lectures: 'What the distribution shows',
    lecturesChapo: 'The analysis’s readings of the actors as a whole. Choose an actor: on the right, their position.',
    lectureEcart: 'Influence and visibility',
    lectureVictimes: 'What the victims endure',
    scenariosDeLActeur: 'The scenarios where they matter',
    voirFiche: 'See their profile',
    profilsConstat: { one: '{n} affected profile', other: '{n} affected profiles' },
    plusTouche: 'Most affected: {profil}, {n}/10',
    ecartConstat: 'A {n}/100 gap between what the media say and what is said online',
    recitsQuiMontent: 'Rising narratives',
    parPlateforme: 'By platform',
    sourcesConstat: { one: '{n} source', other: '{n} sources in {cats} families' },
    sourcesChapo: 'From most to least cited. For each: its reliability rated out of 10, its bias, and what it carries in the analysis.',
    sourcesAide: 'Choose a source: on the right, what it is and where the analysis cites it.',
    plusCitee: 'Most cited: {media}, by {n} items of the analysis',
    toutes: 'All',
    fiabilite: 'Reliability rated out of 10',
    citeeParN: { one: 'cited by {n} item', other: 'cited by {n} items' },
    citeeNullePart: 'cited by no detailed item',
    citeeNullePartLong: 'No fact, narrative, event, precedent, scenario or profile cites it by its tag. It may feed the introduction or the framing.',
    quiParle: 'Who is speaking',
    passagesRetenus: 'The passages the analysis kept',
    ouCitee: 'Where the analysis cites it',
    lieu: {
      fait: { one: '{n} established fact', other: '{n} established facts' },
      recit: { one: '{n} narrative', other: '{n} narratives' },
      chronologie: { one: '{n} event', other: '{n} events' },
      precedent: { one: '{n} precedent', other: '{n} precedents' },
      scenario: { one: '{n} scenario', other: '{n} scenarios' },
      impact: { one: '{n} affected profile', other: '{n} affected profiles' },
    },
    perspectivesManquantes: 'Missing points of view',
    biais: 'Reported biases',
    acces: 'What could not be consulted',
    aSuivre: 'To watch',
    aSuivreChapo: 'The scenarios’ deadlines, and the little-known events of the timeline.',
    echeances: 'Upcoming deadlines',
    peuConnusTitre: 'Little known',
    rienASuivre: 'No upcoming deadline.',
    pourquoiPeuConnu: 'Why it is little known',
    portee: 'What it changed',
    sansSource: 'No source cited.',
    avant: 'Before',
    apres: 'After',
  },
  faits3: {
    rubriques: 'Sections of the tab',
    contexte: 'Context',
    vue: 'Overview',
    groupeEtabli: 'What is established',
    groupeDivise: 'What divides',
    groupePlus: 'See also',
    faitsEtablis: 'Established facts',
    fractures: 'The fault lines',
    debat: 'What the debate leaves out',
    recits: 'The narratives',
    vueTitre: 'What is established, what divides',
    vueChapo: 'The facts the sources establish, and what each narrative makes of them. Each finding opens the section that details it.',
    nFaits: { one: '{n} established fact', other: '{n} established facts' },
    niveau: { high: 'Certain', medium: 'Likely', low: 'Fragile' },
    niveauTitre: { high: 'Established with certainty', medium: 'Likely', low: 'Fragile' },
    nNiveau: {
      high: { one: '{n} certain', other: '{n} certain' },
      medium: { one: '{n} likely', other: '{n} likely' },
      low: { one: '{n} fragile', other: '{n} fragile' },
    },
    nContestes: { one: '{n} disputed by at least one source', other: '{n} disputed by at least one source' },
    socleTitre: 'The common ground',
    socleConstat: { one: '{n} fact whose sources every narrative cites', other: '{n} facts whose sources every narrative cites' },
    socleAucun: 'No fact whose sources every narrative cites',
    socleChapo: 'Opposing readings cite the same sources on these facts: the disagreement lies elsewhere.',
    propresTitre: 'What each narrative keeps to itself',
    propresChapo: 'The facts whose sources only one narrative cites.',
    seulRecit: 'Only narrative {n} cites its sources',
    matriceTitre: 'Which narratives cite the sources of which facts',
    matriceAide: 'A dot: the narrative cites at least one source of the fact; the bigger it is, the more sources. Citing a source is not endorsing it.',
    recitN: 'Narrative {n}',
    nSources: { one: '{n} source', other: '{n} sources' },
    ceQueDisent: 'What its sources say',
    sansCitation: 'The analysis kept no passage from this source for this fact.',
    elements: 'The evidence retained',
    quiConteste: 'Who disputes it',
    nonConteste: 'No source disputes it.',
    recitsQuiCitent: 'The narratives that cite its sources',
    aucunRecit: 'No narrative cites its sources.',
    lireRecit: 'Read the narrative',
    reperesAide: 'Choose a numbered marker: on the right, the source it cites.',
    sourcesCitees: 'Sources cited',
    faitsMemesSources: 'Established facts on the same sources',
    aucunFaitMemesSources: 'No established fact rests on these sources.',
    faitsDuRecit: 'Established facts whose sources it cites',
    aucunFaitDuRecit: 'It cites the sources of no established fact.',
    contredit: { one: '{n} item contradicts it', other: '{n} items contradict it' },
    recitsTitre: { one: '{n} narrative on the same matter', other: '{n} narratives on the same matter' },
    recitsChapo: 'Choose a narrative: on the right, the established facts whose sources it cites.',
    faitsCites: { one: '{n} established fact cited', other: '{n} established facts cited' },
  },
  acteur3: {
    rubriques: 'Sections of the profile',
    contexte: 'Context',
    chargement: 'Loading…',
    groupePublie: 'What is established',
    groupeReseau: 'Network',
    groupeActu: 'In the news',
    publications: 'Our publications',
    reseau: 'Documented relationships',
    organisations: 'Memberships',
    positions: 'Recorded positions',
    veille: 'Monitoring',
    publiTitre: { one: '{n} publication', other: '{n} publications' },
    publiChapo: 'Choose a publication to see, on the right, how it connects to the rest of the profile.',
    consacrees: 'About {nom}',
    analysesRole: 'Analyses in which {nom} plays a part',
    verifications: 'Fact-checked claims',
    analyse: 'Analysis',
    verification: 'Fact-check of a claim',
    comptage: { one: '{n} notable commitment: {detail}', other: '{n} notable commitments: {detail}' },
    nTenus: { one: '{n} kept', other: '{n} kept' },
    nTrahis: { one: '{n} betrayed', other: '{n} betrayed' },
    nIntermediaires: { one: '{n} in between', other: '{n} in between' },
    role: 'Role: {role}',
    leConstat: 'The main finding',
    laQuestion: 'The question left open',
    lEssentiel: 'The gist of the analysis',
    leVerdict: 'The verdict',
    reseauBarometre: 'Also assessed by a barometer, in the network',
    aussiDansAnalyse: 'Also in this analysis, and in the network',
    aucunCroisement: 'No one from the documented network plays a part in it.',
    ouvrirBarometre: 'Open the barometer',
    lireInvestigation: 'Read the investigation',
    ouvrirAnalyse: 'Open the analysis',
    lireVerification: 'Read the fact-check',
    ouvrirFiche: 'Open the profile of {nom}',
    ouvrirOrg: 'Open the organisation’s profile',
    citeesTitre: 'Named in this person’s relationships, not established',
    citeesAide: 'Organisations that this person’s network mentions, with no membership or participant list placing them there. A mention is not a membership: each is given with the relationship that carries it and its source.',
    citeesNonEtablie: { one: 'not established · named in {n} relationship', other: 'not established · named in {n} relationships' },
    citeesFait: 'reported fact',
    citeesLecture: 'our reading',
    citeesSource: 'source ↗',
    reseauTitre: { one: '{n} person', other: '{n} people' },
    reseauChapo: 'Choose a person to see, on the right, what the relationship rests on and where the two profiles meet.',
    depuis: 'since {annee}',
    ceQuiFonde: 'What the relationship rests on',
    lecture: 'interpretation, not an established fact',
    dossierRetient: '{nom}’s file notes: “{intitule}”',
    etabliPar: 'Established by {qui}',
    sansSource: 'no source cited for this link',
    seCroisent: 'Where the two profiles meet',
    membresTousDeux: 'Organisation · both are members',
    analyseCommune: 'Analysis · a part for each',
    aSonBarometre: 'Has a barometer too',
    rienEnCommun: 'No other connection on the site besides this relationship.',
    orgsTitre: { one: '{n} organisation', other: '{n} organisations' },
    orgsChapo: 'Choose an organisation to see, on the right, its other members who are also in the network, and the meetings where attendance is recorded.',
    membresRepertories: { one: '{n} other recorded member', other: '{n} other recorded members' },
    dontReseau: { one: 'including {n} in the network', other: 'including {n} in the network' },
    poste: 'Recorded position',
    membresDuReseau: 'Also members, and in the network',
    aucunMembreReseau: 'No other recorded member appears in the documented relationships.',
    rencontres: 'Meetings where attendance is recorded',
    orgNonPubliee: 'This organisation’s profile is not published yet.',
    positionsTitre: { one: '{n} recorded position', other: '{n} recorded positions' },
    positionsChapo: 'What our analyses and monitoring have recorded, dated and sourced. Choose a position to see its source.',
    laPosition: 'The position',
    enFrancais: 'Text in French: recorded positions are not translated yet.',
    source: 'Source',
    sansSourcePosition: 'No source address for this position.',
    analyseLiee: 'Recorded in the analysis',
    veilleTitre: 'What monitoring has recorded',
    vue: 'Overview',
    vueTitre: 'What the profile establishes',
    vueChapo: 'Findings drawn from our publications and records. Each one opens the section that details it.',
    promesses: 'Promises',
    investigation: 'Investigation',
    analysesVerifs: 'Analyses and fact-checks',
    fil: 'News timeline',
    barometreDate: 'Promise tracker · {date}',
    investigationDate: 'Investigation · {date}',
    reseauConstat: { one: '{n} documented person', other: '{n} documented people' },
    dontCercles: { one: ', {n} of them in the investigation’s circles', other: ', {n} of them in the investigation’s circles' },
    orgConstat: '{org}: {k} of the {m} other members are in their network',
    filConstat: 'Latest on {date}: {titre}',
    anaRole: { one: 'A role in {n} analysis', other: 'A role in {n} analyses' },
    nVerifs: { one: '{n} claim checked', other: '{n} claims checked' },
    nRattachees: { one: '{n} timeline entry linked', other: '{n} timeline entries linked' },
    partoutTitre: 'The people who keep coming back',
    partoutChapo: 'Cross-referencing documented relationships, the investigation’s circles, organisations, analyses and trackers.',
    partoutVide: 'No one in their network crosses the profile elsewhere than through the relationship itself.',
    nCroisements: { one: '{n} cross-reference', other: '{n} cross-references' },
    badgeRelation: 'Relationship',
    badgeInvestigation: 'Investigation',
    badgeAnalyse: 'Analysis',
    badgeBarometre: 'Own tracker',
    laParole: 'The words',
    conclusion: 'What the tracker concludes',
    depuisBarometre: 'What the news timeline links to it',
    aucunePositionPromesse: 'No timeline entry is linked to this commitment.',
    rapprochement: 'Possible link',
    rapprochementAide: 'Link suggested by an automated pass with medium confidence: read it as a lead.',
    chiffrable: 'measurable',
    qualitatif: 'qualitative',
    autresAngles: 'Other angles of the tracker',
    lesCercles: { one: 'The circles established by the investigation · {n} person or entity', other: 'The circles established by the investigation · {n} people and entities' },
    cerclesPremium: { one: 'The person or entity in their circles is reserved for subscribers.', other: 'The {n} people and entities in their circles are reserved for subscribers.' },
    dansInvestigation: 'In the investigation · {cercle}',
    relationDocumentee: 'Documented relationship',
    investigationSur: 'Investigation into {nom}',
    cercleNomme: '“{cercle}” circle',
    sansFiche: 'No profile on the site yet.',
    tous: 'All',
    toutes: 'All',
    legendeAnneaux: 'Inner ring: inner circle · middle: political · outer: business, media, international, unexpected',
    reseauKicker: 'Network · documented relationships and the investigation’s circles',
    reseauTitreE2: { one: '{n} person or entity', other: '{n} people and entities' },
    orgsTitreE2: 'Where their network meets',
    orgsChapoE2: 'Choosing an organisation highlights, in the graph, those of its members who are in their network.',
    membreDepuis: 'Member · {periode}',
    enMemeTemps: 'Members at the same time',
    enMemeTempsAide: 'Members whose dated membership overlaps with theirs. Sitting in the same organization is not a documented relationship.',
    legendeEnMemeTemps: 'dashed: members at the same time, no documented relationship',
    depuisAn: 'since {an}',
    enAn: 'in {an}',
    aussiReseau: 'also in their network',
    duReseau: '{n} in the network',
    filTitreE2: 'From {debut} to {fin}',
    nature: {
      contradiction: 'Contradiction',
      media: 'Appearance',
      role_change: 'Appointment',
    },
    nNatures: {
      contradiction: { one: '{n} contradiction', other: '{n} contradictions' },
      media: { one: '{n} media appearance', other: '{n} media appearances' },
      role_change: { one: '{n} appointment', other: '{n} appointments' },
    },
    rattacheAnalyse: 'Analysis this entry belongs to',
    rattachePromesse: 'Tracker commitment it touches',
    positionsRattachees: 'Timeline entries linked to this analysis',
    analyseDeLaVerif: 'Analysis it belongs to',
    analysesFil: 'The analyses their news timeline leads to',
    entree: 'Who brought them in',
    trouveDans: 'Found in: {ou}',
    ouRelation: 'a documented relationship',
    ouInvestigation: 'the investigation’s circles',
  },

  partages: {
    voirMoins: 'Show less',
    premiumReserve: 'Content for Premium subscribers',
    premiumSansAcces: 'Your current account does not give access to this content. A Premium subscription is required.',
    premiumConnexion: 'This content is for Premium subscribers. Log in if you already subscribe.',
    seConnecter: 'Log in',
    devenirPremium: 'Go Premium →',
    retirerPortfolio: 'Remove from portfolio',
    sauverPortfolio: 'Save to my portfolio',
    enregistre: 'Saved',
    sauvegarder: 'Save',
    reseauApercu: 'Network — preview',
    apercuDe: 'Preview of {nom}’s network',
    reseauDocumente: 'Documented network',
    ouvrirGraphe: 'Open in The Graph →',
    historique: 'Profile history',
    historiqueTexte: 'This profile is updated continuously by our monitoring. Every change is logged here, with sources.',
    voirEntrees: { one: 'See {n} more entry', other: 'See {n} more entries' },
    signalRecent: 'Recent signal',
    exploite: 'used',
    confirme: 'confirmed',
    nouvellesRecentes: 'Recent news ({n})',
    voirNouvelles: { one: 'See {n} more item', other: 'See {n} more items' },
    contradictions: 'Contradictions detected ({n})',
    voirContradictions: { one: 'See {n} more contradiction', other: 'See {n} more contradictions' },
  },

  proposer: {
    rayonx: {
      kicker: 'A claim to check?',
      sub: 'Submit it — the community votes, we put it through its paces.',
      formTitle: 'Submit a claim to RayonX',
      titlePlaceholder: 'The claim to check',
      descPlaceholder: 'Context / source (optional)',
    },
    geo: {
      kicker: 'A topic to unpack?',
      sub: 'Suggest it — the community votes, we analyse it from several angles.',
      formTitle: 'Suggest an analysis',
      titlePlaceholder: 'The topic to analyse',
      descPlaceholder: 'Why is this topic relevant? (optional)',
    },
    actor: {
      kicker: 'Someone to scrutinise?',
      sub: 'Suggest them — the community votes, we study their consistency and networks.',
      formTitle: 'Suggest a person',
      titlePlaceholder: 'Name of the person',
      descPlaceholder: 'Why this person? (optional)',
    },
    org: {
      kicker: 'An organisation to map?',
      sub: 'Suggest it — the community votes, we map its networks of influence.',
      formTitle: 'Suggest an organisation',
      titlePlaceholder: 'Name of the organisation',
      descPlaceholder: 'Why this organisation? (optional)',
    },
    echecEnvoi: 'The suggestion could not be sent.',
    erreurReseau: 'Network error, please try again.',
    erreurN: 'Error {n}',
    envoyee: 'Suggestion sent — it joins the Agora, put to the vote.',
    lancee: 'Analysis started.',
    voirResultat: 'See the result →',
    voirAgora: 'See the Agora →',
    autre: 'Suggest another',
    proposer: 'Suggest →',
    connectez: 'Log in to suggest →',
    region: 'Region',
    domaine: 'Field',
    annuler: 'Cancel',
    envoi: 'Sending…',
    soumettre: 'Submit the suggestion',
    lancement: 'Starting…',
    executer: 'Run now',
  },

  orientation: {
    releves: {
      media: 'Media appearance',
      contradiction: 'Gap noted',
      role_change: 'Change of role',
      autre: 'Note',
    },
    appartenances: {
      core_member: 'member',
      alumni: 'former member',
      invitee: 'guest',
      observer: 'observer',
      board_member: 'board member',
    },
    liens: {
      inner_circle: 'inner circle',
      political: 'political link',
      corporate: 'business link',
      media: 'media link',
      family: 'family link',
      financial: 'financial link',
      international: 'international link',
      institutional: 'institutional link',
      autre: 'link',
    },
    laFiche: 'the {nom} profile',
    uneFicheOrg: 'an organisation profile',
    lienAvec: '{type} with {sujet}',
    dossierRetient: ', which the file describes as “{libelle}”',
    analyseReseau: 'our analysis of {nom}’s network',
    lienNonQualifie: 'unqualified link',
    depuis: ' (since {date})',
    investigationSur: 'the investigation into {sujet}',
    unAutreActeur: 'another person',
    cercle: ', circle “{cercle}”',
    evenement: 'event',
    notreAnalyse: 'our analysis “{titre}”',
    uneDeNosAnalyses: 'one of our analyses',
    analyseOuNom: 'our analysis, where the name appears',
    revientDans: ' — the name recurs in {n} of its sub-investigations',
    anatomieOuNom: 'our anatomy, where the name is listed',
    limiteRienEcrit: 'Nothing here was written about this person: every line was produced while analysing someone or something else.',
    limitePointDeVue: { one: 'The point of view is that of the {sujets} file — that is where these links were noted, and they describe what that file retains.', other: 'The point of view is that of the {sujets} files — that is where these links were noted, and they describe what those files retain.' },
    limiteSansAdresse: '{n} item(s) out of {total} carry no verifiable link: they are marked as such.',
    limiteAbsence: 'The absence of a fact here does not mean it does not exist: it means we have not established it.',
    cadrage: 'We have no analysis devoted to {nom}. This page gathers what our other work has come across about them, each time with the file that establishes it and the link to check it. It is not a portrait, and these lines added together are not a conclusion.',
    fiche: {
      etabliPar: 'established by',
      lecture: '· a reading, not established as fact',
      aucuneAdresse: 'no verifiable link',
      reperes: 'Pointers — not an analysis of {nom}',
      fonction: 'Role noted in our data:',
      releves: 'Noted in our analyses',
      relevesSous: 'Recorded and dated over the course of our work, each with its nature.',
      appartenances: 'Memberships',
      appartenancesSous: 'Taken from the profiles of the organisations concerned.',
      liens: 'Links noted',
      liensSous: 'Described from the point of view of the file that established them, never from this person’s.',
      cercles: 'Investigation circles',
      cerclesSous: 'Position in the circle of someone we have investigated.',
      evenements: 'Events',
      evenementsSous: 'Attendance recorded on organisation profiles.',
      mentions: 'Mentioned in our work',
      mentionsSous: 'The name appears there — to be read for context, not as a conclusion.',
      assemble: 'Assembled on {date} from {natures} kind(s) of material, including {sourcees} item(s) with a verifiable link.',
      rienPublie: 'We have published nothing on this person',
      rienPublieTexte: '{nom} is in our data because one of our pieces of work cited the name. We have produced no analysis, no documented link and no source about them.',
      absence: 'This absence says nothing about the person: it says what we have not done.',
    },
  },

  acteurs: {
    tous: 'All',
    kicker: 'Public figures',
    kickerAnalysees: 'Public figures · {n} analysed',
    titre: 'The faces of power',
    barometresPublies: { one: 'barometer published', other: 'barometers published' },
    notesGlobales: 'The {n} overall scores of the published barometers',
    recherche: 'Name, country, role…',
    type: 'Type',
    analyse: 'Analysis',
    toutes: 'All',
    barometre: 'Barometer',
    investigation: 'Investigation',
    prochesDAbord: 'Your region first',
    plusContradictoires: 'Most contradictory first',
    plusCoherents: 'Most consistent first',
    nomAZ: 'Name, A to Z',
    nbPersonnalites: { one: '{n} public figure', other: '{n} public figures' },
    aucunCorrespond: 'No one matches',
    filtresRestrictifs: 'Your filters may be too narrow. Broaden the search or reset them.',
    reinitialiser: 'Reset the filters',
    rienPourLinstant: 'Nothing to show yet',
    bientot: 'The first profiles are coming soon.',
    noteSur10: 'barometer score out of 10',
    metaTitre: 'People',
    metaDescription: 'Politicians, diplomats, lobbyists — full profiles with consistency score and network of influence.',
    metaIntrouvable: 'Person not found',
    metaPersonnalite: 'Public figure',
    metaProfil: '{type} — Profile, consistency score and network of influence analysed by Lumière Noire.',
  },

  filtres: {
    effacerRecherche: 'Clear the search',
    tri: 'Sort',
    filtresEtTri: 'Filters and sort',
    retirer: 'remove',
    toutEffacer: 'Clear all',
  },

  divers: {
    reserveAbonnes: 'For subscribers.',
    seConnecter: 'Log in',
    sAbonner: 'Subscribe →',
    voirWikimedia: 'See the file and its licence on Wikimedia',
    nonLisible: 'Organisation cited in our analyses — its profile has not been analysed yet.',
    sommaire: 'Contents',
    chargement: 'Loading…',
    derniereMaj: 'Last updated: {date}.',
    aCompleter: 'TO BE COMPLETED',
  },

  rayonx: {
    domaines: {
      politique: 'Politics',
      'économie': 'Economy',
      'santé': 'Health',
      justice: 'Justice',
      environnement: 'Environment',
      science: 'Science',
      'société': 'Society',
      international: 'International',
      autre: 'Other',
    },
    tri: {
      date_desc: 'Newest',
      date_asc: 'Oldest',
      score_desc: 'Highest scored',
      score_asc: 'Lowest scored',
    },
    statuts: {
      processing: 'Being checked',
      failed: 'Check failed',
      draft: 'Private',
      pending_review: 'Under review',
      published: 'Published',
      rejected: 'Rejected',
    },
    actions: {
      demander: {
        bouton: 'Request publication',
        explication: 'Your RayonX goes to review. It stays private until the team decides: if published, everyone will see it and it may appear on the home page; if rejected, you will be told why.',
        confirmer: 'Send for review',
      },
      annuler: {
        bouton: 'Cancel the request',
        explication: 'The review request is withdrawn. Your RayonX stays private.',
        confirmer: 'Cancel the request',
      },
      retirer: {
        bouton: 'Make private again',
        explication: 'Your RayonX leaves the public site. To publish it again, it will have to go through review again.',
        confirmer: 'Make private again',
      },
    },
    actionEchouee: 'The action did not go through.',
    erreurReseau: 'Network error.',
    visibleTous: 'Visible to everyone',
    visibleVous: 'Visible only to you',
    motifRefus: 'Reason for rejection:',
    neRienFaire: 'Do nothing',
    mesRayonx: 'My RayonX checks',
    rayonxPublics: 'Public RayonX checks',
    rayonx: 'RayonX checks',
    rechercheChamp: 'A claim, a person…',
    domaine: 'Field',
    tous: 'All',
    compte: '{n} RayonX',
    pasEncore: 'You have not run a check yet.',
    aucunFiltre: 'No check for this filter.',
    utilisezAgora: 'Use the “Run now” button in the Agora to check a claim.',
    aucunPublic: 'No public check yet.',
    onglets: {
      verdict: 'Verdict',
      preuves: 'Evidence',
      limites: 'Limitations',
      methode: 'Method',
    },
    methodePerimetre: 'Method & scope',
    formeAffirmative: 'Affirmative form checked',
    reformulee: 'Claim restated (neutral)',
    notesMethodo: 'Methodological notes',
    perimetreAnalyse: 'Scope analysed',
    periode: 'Period',
    perimetreGeo: 'Geographical scope',
    termesCles: 'Key terms',
    recherches: 'Searches run ({n})',
    recherchesPlus: { one: '+ {n} more search →', other: '+ {n} more searches →' },
    verification: 'Check',
    nbPreuves: { one: '{libelle} · {n} piece of evidence', other: '{libelle} · {n} pieces of evidence' },
    verdictComplet: 'The full verdict ↓',
    preuvesUneParUne: { one: 'The evidence, in detail ↓', other: 'The {n} pieces of evidence, one by one ↓' },
    lienCopie: '✓ Link copied',
    partager: 'Share',
    pointsCles: 'Key points',
    preuvesCollectees: 'Evidence collected',
    aucunePreuve: 'No evidence indexed',
    limitesEntites: 'Limitations & entities',
  },

  preuves: {
    polarite: {
      confirms: 'Confirms',
      refutes: 'Refutes',
      nuance: 'Qualifies',
    },
    poids: {
      eleve: 'high',
      modere: 'moderate',
      faible: 'low',
    },
    types: {
      official: 'Official',
      media: 'Press',
      academic: 'Academic',
      factchecker: 'Fact-checker',
      legal: 'Legal',
      other: 'Other',
    },
    filtres: {
      all: 'All',
      confirms: 'Confirm',
      refutes: 'Refute',
      nuance: 'Qualify',
    },
    preuveN: 'Evidence {n}',
    preuveNMin: 'evidence {n}',
    poidsLibelle: 'Weight {poids}',
    aucuneCategorie: 'No evidence in this category',
    allerA: 'Go to evidence {n}',
    voirPreuveN: 'See evidence {n}',
    voirDansPreuves: 'See in the evidence →',
    uneCase: 'One square per piece of evidence. Click a colour to keep only that evidence.',
  },

  verdictRx: {
    confiance: {
      high: 'High confidence',
      medium: 'Medium confidence',
      low: 'Low confidence',
    },
    questionPosee: 'Question asked',
    affirmationVerifiee: 'Claim checked',
    contexte: 'Context: {texte}',
    verifieLe: '· Checked on {date}',
    raisonnement: 'Detailed reasoning',
    parSousAffirmation: 'Verdict by sub-claim',
    pourraitChanger: 'The verdict could change if…',
    nuances: 'Caveats to keep in mind',
    entites: 'Entities involved',
    acteurs: 'People',
    organisations: 'Organisations',
    aucuneLimite: 'No limitation or entity identified',
    roles: {
      subject: 'Subject',
      witness: 'Witness',
      source: 'Source',
    },
    exact: 'What is accurate',
    inexact: 'What is inaccurate or missing',
    preuvesUneParUne: { one: 'The evidence, in detail →', other: 'The {n} pieces of evidence, one by one →' },
  },
  rx3: {
    sommaire: 'RayonX contents',
    entete: 'The verdict and its evidence',
    verdict: 'The verdict',
    sous: 'The sub-claims',
    preuves: 'The evidence',
    methode: 'What it rests on',
    sousSous: 'The claim broken down into verifiable parts, each with its own verdict.',
    preuvesSous: { one: '{n} piece of evidence, sorted by direction. Choose one: on the right, its source and what it supports.', other: '{n} pieces of evidence, sorted by direction. Choose one: on the right, its source and what it supports.' },
    methodeSous: 'How the claim was understood, whom the check draws on, and where we searched.',
    reserves: 'Caveats',
    toutes: 'All evidence',
    appuie: 'What it supports in the verdict',
    citeeParSous: 'Cited by the sub-claim',
    ouvrirSource: 'Open the source',
  },

  rayonxListe: {
    kicker: 'RayonX · checks, with evidence',
    titre: 'True, false, or more complicated?',
    legende: { one: 'claim put under the X-ray', other: 'claims put under the X-ray' },
    metaDefaut: 'Analysis and source checking by Lumière Noire.',
  },

  analyse: {
    fermerMaj: 'Dismiss this update',
    detecteAuto: '{date} — detected automatically',
    majMajeures: { one: 'Major update', other: 'Major updates' },
    reduire: 'Show less',
    voirLes: 'See all {n}',
    autresMaj: { one: '+ {n} more update', other: '+ {n} more updates' },
    chargement: 'Loading…',
    impossible: 'This analysis could not be loaded.',
    sommaire: {
      chronologie: 'Timeline events',
      historique: 'Historical precedents',
      scenarios: 'Outlook',
      impact: 'Citizen impact profiles',
      resonance: 'Dominant online sentiment',
      sources: 'Documented sources',
    },
    analyses: 'Analyses',
    analyse: 'Analysis',
    consensusLectures: 'consensus · {n} competing readings',
    version: 'Version {v} · {date}',
    versionLien: 'Version {v} →',
    pied: 'Lumière Noire · Multi-perspective geopolitical analysis · {date}',
    onglets: {
      passe: 'The facts',
      lectures: 'Perspectives',
      plus: 'Go further',
    },
    sectionsAria: 'Sections of the analysis',
    essentiel: {
      aria: 'The gist of the analysis',
      titre: 'The gist',
      uneMinute: '1 min read',
      etabli: 'What is established',
      divise: 'What divides',
      probable: 'Most likely outlook',
      suivi: '{initiale}% at the time of the analysis · reviewed {date}',
      suiviJamais: '{initiale}% at the time of the analysis',
      lireComplete: 'Read the full analysis ↓',
      confiance: {
        high: 'High confidence',
        medium: 'Medium confidence',
        low: 'Low confidence',
      },
    },
    fraicheur: {
      nonVerifie: 'Not checked',
      aJour: 'Up to date',
      recommandee: 'Update recommended',
      aVerifier: 'Data to check',
      couverture: 'Coverage: {date}',
      verifieLe: 'Checked on {date}',
    },
    rendezVous: {
      aria: 'Upcoming milestones in the analyses',
      titre: 'Upcoming milestones',
      sous: 'the nearest dated scenario for each topic',
      probabilite: 'Probability estimated by the analysis',
    },
    liste: {
      kicker: 'Analyses · dated scenarios',
      titre: 'Cross-readings',
      sujetsSuivis: { one: 'topic followed', other: 'topics followed' },
      recherche: 'Search the analyses…',
      region: 'Region',
      domaine: 'Field',
      statut: 'Status',
      toutes: 'All',
      tous: 'All',
      publies: 'Published',
      brouillons: 'Drafts',
      plusRecentes: 'Newest',
      plusConsensuelles: 'Most consensual',
      plusDisputees: 'Most disputed',
      aucune: 'No analysis matches',
      bientot: 'The first geopolitical analyses are coming soon.',
      deConsensus: 'consensus',
      metaDescription: 'In-depth geopolitical analyses — conflicts, power trajectories, crises. Decoding facts, not opinions.',
      introuvable: 'Analysis not found',
    },
  },

  plusLoin: {
    chronologie: 'Timeline',
    chronologieSub: '{n} events · {caches} little known',
    historique: 'Historical depth',
    historiqueSub: '{n} precedents · “Then / Now” comparisons',
    impact: 'Citizen impact',
    impactSub: '{n} concrete profiles',
    resonance: 'What social media says',
    resonanceSub: '{n} dominant sentiments',
    resonanceEcart: ' · gap with the media {ecart}/100',
    acteurs: 'Actors',
    acteursSub: 'Visible · Hidden · Victims · Beneficiaries',
    scenarios: 'What now?',
    scenariosSub: '{n} possible scenarios · probabilities · triggers',
    sources: 'All sources',
    sourcesSub: '{n} sources · {cats} categories · scores /10',
    evolution: 'Evolution',
    retour: '← Go further',
    titre: 'Go further',
    premiumEvolution: 'Evolution analysis is for Premium subscribers',
    premiumActeurs: 'Actor analysis is for Premium subscribers',
    premiumScenarios: 'Scenarios are for Premium subscribers',
    premiumImpact: 'Citizen impact is for Premium subscribers',
  },

  lectures: {
    rechercheRien: 'What was looked for behind this account: no documented network',
    rechercheN: { one: 'What was looked for behind this account: {n} documented network', other: 'What was looked for behind this account: {n} documented networks' },
    biaisFaible: 'Low bias',
    biaisEleve: 'High bias',
    biaisMoyen: 'Medium bias',
    nbSources: '{n} sources',
    ceQueDit: 'What this reading says',
    detail: 'The detail, argument by argument',
    argumentN: 'Argument {n}',
    preuvesAppui: '{n} pieces of supporting evidence',
    contradictoires: '{n} contradicting elements',
    anglesMorts: '{n} blind spots',
    contexte: {
      recits: 'The readings',
      these: 'The thesis',
      aide: 'Pick an argument: what supports it appears on the right.',
      titre: 'Context',
      source: 'Source {n}',
      retrouve: 'Found in the page',
      pourPhrase: 'for the sentence “{debut}”',
      retenu: 'Passage kept by the analysis',
      pasRetrouve: 'The machine did not find this sentence in the page.',
      illisible: 'The machine could not read the page.',
      chiffresOk: 'Figures found: {liste}',
      chiffresManquants: 'Not in this passage: {liste}',
      enLangue: 'in {langue}',
      donnees: 'The argument’s data',
      cliquer: 'A marker in the text shows the passage for its sentence.',
      porte: 'What supports it',
      sansSource: 'No source cited for this argument.',
      sansPassage: 'No passage kept from this source.',
      ouvrir: 'Open the source',
      faits: 'Established facts resting on the same sources',
      sansFait: 'None of the analysis’s established facts rests on these sources.',
      nonConteste: 'Uncontested',
      contestePar: { one: 'Contested by {n} source', other: 'Contested by {n} sources' },
      toutLeRecit: 'For the whole reading',
    },
    relayeePar: 'Amplified by',
    construite: 'How this reading is built',
    perspectives: 'The perspectives',
    premium: 'Further perspectives are for Premium subscribers',
    choisir: 'Choose a perspective',
    precedent: 'Previous',
    suivant: 'Next',
  },

  typo: {
    ouvrant: '“',
    fermant: '”',
  },

  evolution: {
    importance: {
      major: 'Major',
      moderate: 'Moderate',
      minor: 'Minor',
    },
    issues: {
      confirmed: 'Confirmed',
      partially_confirmed: 'Partly confirmed',
      invalidated: 'Invalidated',
      still_pending: 'Pending',
    },
    synthese: '◆ Summary of the evolution',
    perspectives: '⇄ How the perspectives evolved',
    aucunFait: 'No documented change of fact.',
    avant: 'Before',
    maintenant: 'Now',
    aucunActeur: 'No documented shift by an actor.',
    aucuneDonnee: 'No outlook data available.',
    bilan: '◈ How the outlook fared',
    bilanV: '◈ How the v{v} outlook fared',
    depuis: '◈ Since v{v}: the tracked outlook',
    depuisRien: 'Not reviewed since publication.',
    etape: '{date}: {avant}% → {apres}%',
    inattendus: 'Unexpected developments',
    onglets: {
      synthese: 'Summary',
      faits: 'Facts',
      acteurs: 'Actors',
      scenarios: 'Scenarios',
    },
  },

  faits: {
    certitude: 'What we know for certain',
    contestePlusieurs: 'Disputed on several points',
    conteste: 'Disputed',
    fractures: 'Why no one agrees',
    debatNeDitPas: 'What the debate leaves out',
    nbPerspectives: '{n} perspectives on the same affair',
    cliquez: 'Click a perspective to read its detailed analysis',
    lirePerspective: 'Read the perspective: {nom}',
    ceQuiSestPasse: 'What happened',
    pourAllerPlusLoin: 'To go further',
  },

  subActeurs: {
    segments: {
      visibles: 'Visible',
      caches: 'Hidden',
      victimes: 'Victims',
    },
    aucun: { one: 'Networks were looked for behind the account: it has none.', other: 'Networks were looked for behind the {n} accounts: none has any.' },
    tous: { one: 'Networks were looked for behind the account: it has some.', other: 'Networks were looked for behind the {n} accounts: all have some.' },
    partiel: 'Networks were looked for behind the {n} accounts: {avec} have some, {sans} none. The detail is in the Perspectives tab, account by account.',
    discret: 'Low-profile',
    victime: 'Victim',
    agitPlus: 'Acts more than it shows · +{n}',
    influence: 'Influence',
    visibilite: 'Visibility',
    retourActeurs: '← Back to the actors',
    position: 'Position in the affair',
    etabli: 'What is established',
    role: 'Role',
    indicateurs: 'Indicators',
    titre: 'Actors',
    sousTitre: 'Visible · Hidden · Victims · Beneficiaries',
    typeActeurs: 'Type of actors',
    repartition: 'What this breakdown shows',
    echelle: 'Influence and visibility scored out of 100 — influence is weight on the course of events, visibility the space taken in public debate.',
    dynamique: 'The power dynamics, in one reading',
    cadrage: 'Who controls the framing of the debate',
  },

  sub: {
    chronologie: 'Timeline',
    nbEvenements: '{n} events',
    peuConnus: ' · {n} little known',
    peuConnu: 'Little known',
    historique: 'Historical depth',
    precedents: '{n} precedents that shed new light',
    similitudes: 'Similarities',
    differences: 'Differences',
    lecons: 'Lessons for today',
    benefice: 'Benefit',
    impact: 'Impact',
    impactNegatif: 'Negative impact',
    quoi: 'What happened',
    pourquoi: 'Why it affects them',
    auBout: 'The bottom line',
    tousProfils: '← All profiles',
    severite: 'Impact severity',
    pays: 'Country',
    impactCitoyen: 'Citizen impact',
    profilsDans: '{n} concrete profiles in {pays} countries',
    vueEnsemble: 'Overview',
    plusTouche: 'Profiles — most to least affected',
    lireHistoire: 'Read their story',
    plateformes: {
      autres: 'Other platforms',
    },
    ecart: {
      separees: 'Two separate conversations',
      differents: 'Markedly different framings',
      divergences: 'A few divergences',
      proches: 'Similar framings',
    },
    reseaux: 'What social media says',
    ressentis: '{n} dominant sentiments',
    recitsEmergents: ' · {n} emerging narratives',
    misesEnGarde: ' · {n} caveats',
    ecartMedias: 'Gap between the media and social media — {ecart}/100 · {libelle}',
    ecartAria: 'Framing gap: {ecart} out of 100',
    memesTermes: 'Same terms as the media',
    domine: 'What dominates online',
    parts: 'Shares of the sentiment expressed online, as the analysis splits them out of 100.',
    presence: 'Presence of each sentiment, measured separately: these values do not share a total.',
    fausser: 'What can skew these signals',
    recitsMontent: '{n} rising narratives',
    parPlateforme: 'By platform — {liste}',
    scenariosPossibles: 'Possible scenarios',
    scenariosSous: '{n} possible scenarios · estimated probabilities · triggers',
    declencheurs: 'Possible triggers',
    implications: 'Implications',
    parProbabilite: 'By decreasing probability',
    sources: 'Sources',
    sourcesSous: '{n} sources · {cats} categories · methodological transparency',
    categorieAria: 'Source category',
    corpus: '{n} sources · average score {score}/10',
    duCorpus: ' · {part} of the corpus',
    biaisEleveCertaines: ' · some high-bias sources — see detail',
    contreCourant: 'Against the grain',
    aucuneSource: 'No source in this category.',
    limitesCorpus: 'Limits of this corpus',
  },

  sourcesCat: {
    primarySources: 'Primary sources',
    mainstreamWestern: 'Western mainstream',
    alternativeIndependent: 'Independent / alternative',
    nonWesternPerspectives: 'Non-Western',
    academicWitnesses: 'Academic',
  },

  org: {
    portee: {
      global: 'Global reach',
      regional: 'Regional reach',
      national: 'National reach',
    },
    chargement: 'Loading…',
    nonTrouvee: 'Organisation not found',
    retour: '← Back to organisations',
    organisations: 'Organisations',
    organisation: 'Organisation',
    membresDocumentes: '{n} documented members',
    dontBarometre: ' · {n} with a barometer',
    fondeEn: 'Founded in {date}',
    explorerReseau: 'Explore its network in the graph →',
    metaDescription: 'Think tanks, lobbies, foundations — mapping their members, funding and real objectives.',
    metaIntrouvable: 'Organisation not found',
    metaProfil: '{type} — Network of influence, members and funding analysed by Lumière Noire.',
    tiers: {
      S: 'Maximum influence',
      A: 'Major influence',
      B: 'Significant influence',
      C: 'Moderate influence',
    },
    statutsMembre: {
      founder: 'Founder',
      core_member: 'Member',
      alumni: 'Alumni',
      young_leader: 'Young Leader',
      invitee: 'Guest',
      honorary: 'Honorary',
      emerging: 'Emerging',
      coopted_opponent: 'Co-opted opponent',
      emerging_leader: 'Emerging leader',
    },
    categories: {
      politicalLeaders: 'Politics',
      corporateLeaders: 'Corporate',
      financialSector: 'Finance',
      mediaProfessionals: 'Media',
      academicIntellectuals: 'Academia',
      militaryDefense: 'Defence',
    },
    acces: {
      open: 'Open',
      restricted: 'Restricted',
      closed: 'Closed',
      chatham_house_rule: 'Chatham House Rule',
    },
    q: {
      quoi: 'What is it?',
      quoiSous: 'What it says about itself, what it pursues, where it comes from.',
      membres: 'Who belongs to it?',
      membresSous: '{n} documented members, by category; guests and recruits.',
      membresSousUn: '{n} documented member, by category; guests and recruits.',
      membresSousVide: 'Documented members, guests and recruits.',
      influence: 'How does it weigh on decisions?',
      influenceSous: 'Recommendations acted upon, crises seen coming, key gatherings.',
      argent: 'Where does the money come from?',
      argentSous: 'Who funds it, how the money flows, conflicts of interest.',
      reseau: 'Who does it work with?',
      reseauSous: 'Linked organisations, the members they share.',
      bilan: 'What should we take away?',
      bilanSous: 'The findings, what is problematic, what is a legitimate contribution.',
      ouvert: 'What remains open?',
      ouvertSous: 'What the profile could not establish, the reliability of its sources, leads to follow.',
      sources: 'What is it based on?',
      sourcesSous: { one: '{n} source in the profile’s register.', other: '{n} sources in the profile’s register.' },
      rassemble: 'This question brings together {liste}.',
      rassembleFace: 'This question brings together {liste}, set side by side.',
      rassembleReseau: 'This question brings together {liste} and the members they share.',
      recommandations: { one: '{n} recommendation acted upon', other: '{n} recommendations acted upon' },
      crises: { one: '{n} crisis seen coming', other: '{n} crises seen coming' },
      evenements: { one: '{n} documented gathering', other: '{n} documented gatherings' },
      financements: { one: '{n} source of funding', other: '{n} sources of funding' },
      circuit: 'a documented circular funding loop',
      conflits: { one: '{n} documented conflict of interest', other: '{n} documented conflicts of interest' },
      liens: { one: '{n} linked organisation', other: '{n} linked organisations' },
      constats: { one: '{n} key finding', other: '{n} key findings' },
      problemes: { one: '{n} problematic aspect', other: '{n} problematic aspects' },
      contributions: { one: '{n} legitimate contribution', other: '{n} legitimate contributions' },
      lesRendezVous: 'Key gatherings',
      essentiel: 'The gist',
      sommaire: 'Profile contents',
      enNQuestions: 'The profile in {n} questions',
    },
  },

  menu3: {
    identite: 'Identity',
    influence: 'Influence',
    faits: 'The facts',
    contexte: 'The context',
  },

  orgSecteurs: {
    politicalLeaders: 'Politics',
    corporateLeaders: 'Corporate',
    financialSector: 'Finance',
    mediaProfessionals: 'Media',
    academicIntellectuals: 'Academia',
    internationalOrganizations: 'Int’l orgs',
    militaryDefense: 'Defence',
  },

  orgMembres: {
    saFiche: 'Profile →',
    documentes: 'Documented',
    politiques: 'Politicians',
    corporate: 'Corporate',
    finance: 'Finance',
    tauxPlacement: 'Placement rate',
    delaiPosition: 'Time to position',
    conversion: 'Conversion',
    notreLecture: '{libelle} (our reading)',
    chercher: 'Search for a member…',
    categorie: 'Category',
    periode: 'Period',
    tous: 'All',
    enExercice: 'Current',
    anciens: 'Former',
    nbMembres: { one: '{n} member', other: '{n} members' },
    jusquEn: 'until {annee}',
    ancien: 'former',
    invitesStrategiques: 'Strategic guests',
    invite: 'Guest',
    recruteEnsuite: 'later recruited',
    nonRecrute: 'not recruited',
    opposantsCooptes: 'Co-opted opponents',
    opposantCoopte: 'Co-opted opponent',
    positionInitiale: 'Original position: {texte}',
    leadersEmergents: 'Emerging leaders cultivated',
    leaderEmergent: 'Emerging leader',
  },

  orgEvents: {
    aucun: 'No documented event',
    prochainRun: 'Events will be available after the next pipeline run.',
    documentesSur: 'Events documented over {n} years',
    documentes: 'Documented events',
    avecParticipants: 'With identified participants',
    fermees: 'Closed meetings',
    dateInconnue: 'Unknown date',
    sansEvenement: '{n} years without a documented event',
    annees: 'The {decennie}s',
    voirEvenements: { one: 'See {n} more event', other: 'See {n} more events' },
    dateIncertaine: 'Uncertain date',
    nbParticipants: '{n} participants',
    decisionsCorrelees: { one: '→ {n} correlated decision', other: '→ {n} correlated decisions' },
    sujetsOfficiels: 'Official topics',
    sujetsNonDivulgues: 'Undisclosed topics',
    participants: 'Participants',
    nonConfirme: 'Unconfirmed',
    autres: { one: '+{n} more — see the detail →', other: '+{n} more — see the detail →' },
  },

  sourceRef: {
    retireeTitre: 'The link cited for this entry did not match any valid article format on the site invoked: it has been removed. The membership is still shown, but is no longer backed by anything.',
    atteste: 'A source attests this membership, but no precise link can be cited for this entry.',
    nonEtablieTitre: 'No source consulted attests this membership. The entry is kept, but it cannot be verified as it stands.',
    adresseRetiree: 'link removed',
    attesteSansLien: 'attested, no link',
    nonEtablie: 'source not established',
    pageAccueilTitre: '{url} — the site’s home page: it does not document this particular claim.',
    pageAccueil: 'home page',
    capture: 'archive.org snapshot',
    captureDisparue: ' — the original page no longer responds (404)',
    voirPlus: 'See more →',
  },

  blocs: {
    nature: {
      faitEtLecture: 'Fact and reading',
      fait: 'Fact',
      lecture: 'Reading',
      methode: 'Method',
    },
    canaux: {
      titre: 'The channels',
      sous: 'How its influence travels: official roles, informal relays, people moving back and forth.',
      governmentCoordination: 'With governments',
      corporateCoordination: 'With companies',
      internationalCoordination: 'Internationally',
      officialAdvisoryRoles: 'Official advisory roles',
      informalInfluence: 'Informal influence',
      revolvingDoor: 'Revolving door',
      legislativeInfluence: 'On legislation',
      ceoForums: 'CEO forums',
      industryAlignment: 'Industry alignment',
      regulatoryCapture: 'Regulatory capture',
      multilateralInfluence: 'Multilateral institutions',
      treatyInfluence: 'Treaties',
      globalGovernance: 'Global governance',
    },
    argent: {
      titre: 'Where the money goes',
      sous: 'The main spending items, as documented; amounts are those of the sources, often rounded.',
      total: 'Total spending: {liste}',
      operational: 'Operations',
      programs: 'Programmes',
      events: 'Events',
      lobbying: 'Lobbying',
    },
    reseau: {
      position: 'Its position',
      positionSous: 'Reading the map: where it sits, what it connects.',
      coordination: 'How they coordinate',
      coordinationSous: 'What makes these organisations act together.',
      centralityScore: 'Its place in the network',
      clusterBelonging: 'The circles it belongs to',
      bridgingRole: 'Its bridging role',
      redundancy: 'Redundancy of links',
      temporalCoordination: 'Over time',
      thematicCoordination: 'Through themes',
      personnelRotation: 'Through people',
      fundingCoordination: 'Through money',
    },
    effets: {
      titre: 'What it produces',
      sous: 'Our reading of the effects of its action, based on the profile’s sourced elements.',
      economicImpact: 'On the economy',
      socialImpact: 'On society',
      sovereigntyImpact: 'On sovereignty',
      democraticImpact: 'On democracy',
    },
    ouvert: {
      limites: 'The limits of the data',
      limitesSous: 'What the profile could not establish, and why.',
      appui: 'What to rely on',
      appuiSous: 'The analysis’s own assessment of its sources.',
      pistes: 'Leads to investigate',
      pistesSous: 'What would need checking, and a profile cannot settle on its own.',
      qualiteSources: 'Source quality',
      completude: 'Data completeness',
      risqueBiais: 'Risk of bias',
      niveau: {
        high: 'high',
        medium: 'medium',
        low: 'low',
      },
      niveauF: {
        high: 'high',
        medium: 'medium',
        low: 'low',
      },
      techniques: {
        sourceurl: 'Members with no source link',
        urls: 'Entries with no source link',
        coopted: 'Co-opted opponents',
      },
    },
  },

  orgLibelles: {
    fonction: {
      network_coordination: 'network coordination',
      financial_coordination: 'financial coordination',
      policy_development: 'policy development',
      talent_pipeline: 'leadership pipeline',
    },
    capture: {
      systematic: 'systematic',
      significant: 'significant',
      moderate: 'moderate',
    },
    impact: {
      severe: 'severe',
      significant: 'significant',
      moderate: 'moderate',
    },
    portee: {
      global: 'global',
      regional: 'regional',
      national: 'national',
    },
    gravite: {
      critical: 'critical',
      high: 'high',
      medium: 'medium',
      low: 'low',
    },
    financement: {
      membership_fees: 'membership fees',
      event_revenue: 'event revenue',
      foundation_grants: 'foundation grants',
      government_funding: 'government funding',
      corporate_partnerships: 'corporate partnerships',
      corporate_operations: 'commercial activity',
      product_revenue: 'product sales',
    },
  },

  portrait: {
    mission: 'Official mission',
    objectifs: 'Real objectives',
    tauxPlacement: 'Placement rate',
    orgsLiees: 'Linked orgs',
    conversion: 'Conversion',
    classification: 'SYSTEMIC CLASSIFICATION',
    fonction: 'Function',
    capture: 'Capture',
    impactDemocratique: 'Democratic impact',
    portee: 'Reach',
    phases: 'Phases of development',
    fondation: 'Founding',
    expansion: 'Expansion',
    maturite: 'Maturity',
    contexteFondation: 'Founding context',
    momentHistorique: 'Historical moment',
    recitOfficiel: 'Official account',
    motivations: 'Real motivations',
    financementInitial: 'Initial funding',
    soutien: 'Institutional support',
    fondateurs: 'Founders ({n})',
    fondateur: 'Founder',
    reduire: 'Show less ↑',
    details: 'Details →',
    motivationsAnalysees: 'Motivations analysed',
    connexions: 'Network connections',
    trajectoire: 'Later career',
    structure: 'Organisational structure',
    gouvernance: 'Governance',
    criteres: 'SELECTION CRITERIA',
    categoriesMembres: 'MEMBER CATEGORIES',
    transparence: 'LEVEL OF TRANSPARENCY',
    employes: 'Staff',
    budget: 'Annual budget',
  },

  duree: {
    immediat: 'immediate',
    mois: { one: '{n} month', other: '{n} months' },
    ans: { one: '{n} year', other: '{n} years' },
    environAns: '~{n} years',
    jusqua: 'up to {d}',
    entre: 'between {a} and {b}',
  },

  panneau: {
    presse: {
      closed: 'behind closed doors',
      chatham_house_rule: 'Chatham House Rule',
      open: 'open to the press',
      partial: 'press partly admitted',
    },
    source: 'Source',
    aQuitteEn: 'left in {annee}',
    ancienMembre: 'former member',
    inviteEn: 'invited: {annee}',
    recruteEnsuite: 'later recruited',
    nonRecrute: 'not recruited',
    'cooptéEn': 'co-opted in {annee}',
    repereEn: 'spotted in {annee}',
    positionActuelle: 'Current position',
    plusHautPoste: 'Highest position',
    posteEntree: 'Position on joining',
    poste: 'Position',
    positionInitiale: 'Original position',
    apresCooptation: 'After co-optation',
    loyaute: 'Loyalty',
    anneeEntree: 'Year joined',
    anneeSortie: 'Year left',
    alignement: 'Political alignment',
    decisionsCles: 'Key decisions',
    utilisationReseau: 'Use of the network',
    contexteInvitation: 'Context of the invitation',
    gainOrganisation: 'What the organisation gains',
    relationEnsuite: 'Relationship afterwards',
    strategieCooptation: 'Co-optation strategy',
    neutralisation: 'Neutralisation',
    strategieSuivi: 'Cultivation strategy',
    placementObtenu: 'Placement achieved',
    description: 'Description',
    preuvesCoordination: 'Evidence of coordination',
    membresPartages: 'Notable shared members',
    organisations: 'Organisations',
    fonctionPont: 'Bridging role',
    doubleAppartenance: 'What the dual membership brings',
    adoptee: 'adopted',
    delaiImplementation: 'Implementation timeline',
    impactEconomique: 'Economic impact',
    gouvernementsCibles: 'Target governments',
    preuvesDocumentees: 'Documented evidence',
    beneficiaires: 'Beneficiaries',
    alumniImplementeurs: 'Alumni who implemented it',
    gravite: 'severity {niveau}',
    acteursImpliques: 'Actors involved',
    preuves: 'Evidence',
    premiereDiscussion: 'First discussed',
    criseRealisee: 'Crisis materialised',
    delaiAnticipation: 'Lead time',
    mesuresPreparation: 'Preparatory measures',
    nbParticipants: '{n} participants',
    participants: 'Participants ({n})',
    nonConfirme: 'unconfirmed',
    decisionsCorrelees: 'Correlated decisions ({n})',
    pasCausalite: 'A documented, sourced correlation in time — not an established causal link.',
    suiviImmediat: 'followed immediately',
    suiviApres: 'followed {d} later',
  },

  orgOnglets: {
    deductionTitre: 'Lumière Noire’s inference from the sourced elements of this profile — not a fact reported by a source.',
    notreLecture: 'our reading',
    source: 'source ↗',
    recos: {
      transparencyMeasures: 'Transparency measures',
      democraticSafeguards: 'Democratic safeguards',
    },
    verdictSystemique: 'Systemic verdict',
    anomalies: 'Statistical anomalies',
    constatsCles: 'Key findings',
    problematiques: 'Problematic aspects',
    legitimes: 'Legitimate contributions',
    recommandations: 'Recommendations',
    gravite: 'severity {niveau}',
    sourcesFinancement: 'Sources of funding',
    circulaire: 'Circular funding model',
    circuitCapture: 'Documented capture loop',
    roi: 'Estimated ROI',
    conflits: 'Conflicts of interest ({n})',
    transparence: 'Transparency',
    manquements: { one: '{n} documented breach', other: '{n} documented breaches' },
    adopte: '✓ Adopted',
    nonAdopte: 'Not adopted',
    recommandationsStat: 'Recommendations',
    tauxAdoption: 'Adoption rate',
    delaiMoyen: 'Average lead time',
    penetrationGeo: 'Geographic reach',
    recosPolitiques: 'Policy recommendations',
    anticipationCrises: 'Crisis anticipation',
    orgLiee: 'Linked organisation',
    nbOrganisations: '{n} organisations',
    orgsLiees: { one: '{n} linked organisation', other: '{n} linked organisations' },
    ecosysteme: 'Ecosystem assessment',
    organisationsLiees: 'Linked organisations ({n})',
    laFiche: 'Profile →',
    multiAffilies: 'Members with multiple affiliations ({n})',
    multiAffiliesTexte: 'People present in several organisations of the ecosystem: the coordination nodes between structures.',
    aucuneSource: 'No documented source for this organisation yet.',
    sourcesDocumentees: 'Documented sources',
    avecLien: '{n} sources · {liens} with a direct link',
    tier: 'Tier {tier}',
  },

  orgOrientation: {
    liens: {
      shared_members: 'shared members',
      funding: 'funding',
      coordination: 'coordination',
      ideological_alignment: 'ideological alignment',
      subsidiary: 'subsidiary',
      parent: 'parent company',
    },
    titre: 'Orientation profile — not an analysis',
    etablissent: 'What {n} of our analyses establish',
    sansAdresse: 'This mention carries no source link.',
    personnes: 'People our analyses link to {nom}',
    personnesTexte: 'They appear in our analyses of other organisations. They are not necessarily its leaders, and the list is not exhaustive.',
    releveDans: 'noted in our analysis of',
    nePeutPas: 'What this profile cannot say',
    assemblee: 'Assembled on {date} from {mentions} mention(s) and {sources} source(s). No research of its own, no generated content.',
  },

  orgListe: {
    portees: {
      global: 'Global',
      regional: 'Regional',
      national: 'National',
    },
    tous: 'All',
    toutes: 'All',
    kicker: 'Organisations · networks of influence',
    orientations: { one: ' · {n} orientation profile', other: ' · {n} orientation profiles' },
    titre: 'The circles of power',
    analysees: { one: 'organisation analysed', other: 'organisations analysed' },
    recherche: 'Name, acronym, headquarters…',
    type: 'Type',
    portee: 'Reach',
    analyseesDabord: 'Analysed first',
    plusMembres: 'Most documented members',
    nomAZ: 'Name, A to Z',
    aucune: 'No organisation matches',
    bientot: 'The first mapped organisations are coming soon.',
    bleuPortee: 'The blue follows reach:',
    mondiale: 'global',
    regionale: 'regional',
    nationale: 'national',
    nonRenseignee: 'not stated',
    membresDocumentes: 'documented members',
    ficheOrientation: 'Orientation profile',
    citeePar: ' · cited by {noms}',
  },

  cumulards: {
    aria: 'People present in several organisations',
    titre: { one: '{n} person in several of these organisations', other: '{n} people in several of these organisations' },
    plusPresentes: ' · the {n} most present',
    nbOrganisations: '{n} organisations',
  },

  cartes: {
    ouvrir: '{nom} — open the detail',
    detail: 'Detail →',
  },

  enq: {
    nature: {
      lecture: 'Reading',
      hypothese: 'Hypothesis',
      observation: 'Observation',
      fait: 'Fact',
    },
    source: 'Source ↗',
    plusLoin: 'To go further',
    statuts: {
      en_cours: 'Ongoing proceedings',
      classe: 'Dropped',
      close: 'Closed',
      condamnation: 'Conviction',
      relaxe: 'Acquittal',
      non_lieu: 'Case dismissed',
    },
    declare: 'Stated',
    observe: 'Observed',
    estime: 'Estimated',
    ecart: 'Gap',
    attendu: 'Expected',
    intention: 'Intent',
    coincidence: 'Coincidence',
    systeme: 'System',
    q: {
      qui: 'Who is this?',
      quiSous: 'The career, step by step.',
      justice: 'What is the justice system examining?',
      justiceSous: 'Documented proceedings and their status. Ongoing proceedings establish no guilt.',
      argent: 'Where does the money come from?',
      argentSous: 'Documented flows, wealth, conflicts of interest.',
      argentReserve: { one: '{n} documented flow or conflict of interest, declared and estimated wealth, lifestyle.', other: '{n} documented flows and conflicts of interest, declared and estimated wealth, lifestyle.' },
      entourage: 'Who is around them?',
      entourageSous: 'Close associates, allies, business, press and influence relationships.',
      entourageReserve: { one: '{n} documented relationship: close associates, political allies, business, media, circles of influence.', other: '{n} documented relationships: close associates, political allies, business, media, circles of influence.' },
      recit: 'Who tells their story?',
      recitSous: 'The supportive press, what gets amplified, what disappears.',
      recitReserve: { one: '{n} documented mechanism or narrative: friendly press, amplified topics, sidelined topics.', other: '{n} documented mechanisms and narratives: friendly press, amplified topics, sidelined topics.' },
      colle: 'What doesn’t add up?',
      colleSous: 'What was said and what was done; what departs from the ordinary.',
      colleReserve: { one: '{n} documented contradiction or gap, compared point by point.', other: '{n} documented contradictions and gaps, compared point by point.' },
      ouvert: 'What remains open?',
      ouvertSous: 'What the investigation could not settle, and what it set aside.',
      ouvertReserve: { one: '{n} other question or rumour set aside, and the investigation’s limits.', other: '{n} other questions and rumours set aside, and the investigation’s limits.' },
      sources: 'What is it based on?',
      sourcesSous: { one: '{n} source, in the order the investigation cites it.', other: '{n} sources, in the order the investigation cites them.' },
    },
    blocs: {
      formation: 'Education',
      parcoursPrive: 'Private-sector career',
      chaqueEtape: 'What the investigation notes at each step',
      enquetesEnCours: 'Ongoing investigations',
      recoupement: 'Cross-checking, case by case',
      refutation: 'Rebuttals',
      conflits: 'Documented conflicts of interest',
      patrimoine: 'Wealth',
      transactions: 'Notable transactions',
      construction: 'How it was built, period by period',
      trainDeVie: 'Lifestyle and assets',
      premierCercle: 'The inner circle',
      politique: 'In politics',
      affaires: 'In business',
      presse: 'In the press',
      international: 'Internationally',
      cerclesInfluence: 'Circles of influence',
      liensInattendus: 'Unexpected links',
      cercleRapproche: 'The close circle, official role and real role',
      nominations: 'How appointments are made',
      protection: 'Institutional protection',
      opposants: 'Opponents',
      amplifie: 'What gets amplified',
      disparait: 'What disappears',
      favorables: 'Media described as favourable',
      critiques: 'Media described as critical',
      ordinaire: 'What departs from the ordinary',
      schemas: 'Recurring patterns, and three ways to read them',
      hypotheses: 'Competing hypotheses',
      rumeursEcartees: 'Rumours set aside for lack of corroboration',
      manque: 'What is missing to settle it',
      biais: 'This investigation’s possible biases',
    },
    documentOfficiel: 'Official document',
    essentiel: 'The gist',
    sommaire: 'Investigation contents',
    enNQuestions: 'The investigation in {n} questions',
    legendeNatures: 'each passage says what it is.',
    faitsDocumentes: { one: '{n} documented fact, against received wisdom.', other: '{n} documented facts, against received wisdom.' },
    page: {
      acteurNonTrouve: 'Person not found',
      introuvable: 'Investigation not found',
      nonDisponibles: 'Data not available',
      retour: '← Back to the profile',
      investigation: 'Investigation',
      kicker: 'Investigation · updated on {date}',
      procedures: { one: 'documented legal proceeding', other: 'documented legal proceedings' },
    },
  },

  anatInv: {
    force: {
      strong: 'Strong',
      moderate: 'Moderate',
      weak: 'Weak',
    },
    certitude: {
      high: 'High certainty',
      medium: 'Medium certainty',
      low: 'Low certainty',
    },
    fiabilite: {
      high: 'High reliability',
      medium: 'Medium reliability',
      low: 'Low reliability',
    },
    piste: {
      mechanism_to_expose: 'Mechanism to expose',
      relationship_to_document: 'Relationship to document',
      fact_to_verify: 'Fact to verify',
      timeline_to_reconstruct: 'Timeline to reconstruct',
      actor_to_investigate: 'Person to investigate',
    },
    priorite: {
      high: 'high priority',
      medium: 'medium priority',
      low: 'low priority',
    },
    prioriteBrute: 'priority {p}',
    acteursCles: 'Key people',
    orgsCles: 'Key organisations',
    aucunFait: 'No settled fact for this theme.',
    faitsPivots: { one: 'The documented pivotal fact underpinning the investigation.', other: 'The {n} documented pivotal facts underpinning the investigation.' },
    aucunArgument: 'No argument available.',
    pour: 'For',
    contre: 'Against',
    aucunPour: 'No argument for.',
    aucunContre: 'No argument against.',
    sourceFleche: 'Source →',
    aucunAngle: 'No blind spot identified.',
    anglesIntro: 'Grey areas and questions left open by the current state of the documentation.',
    aucuneChrono: 'No timeline data or lead identified.',
    chronologie: 'Timeline ({n})',
    pistes: 'Leads to follow ({n})',
    pistesIntro: 'What the investigation identifies as the next angles to pursue.',
    faits: 'Facts',
    arguments: 'Arguments',
    anglesMorts: 'Blind spots',
    chronoPistes: 'Timeline & leads',
    pasDeDetail: 'No documented detail yet for this theme.',
    question: 'The question asked',
    conclut: 'What it concludes',
    appuie: 'What supports it',
    limite: 'What limits it',
  },

  preuve: {
    P1: 'Established fact',
    P2: 'Probable fact',
    P3: 'Supported hypothesis',
    P4: 'Plausible hypothesis',
    P5: 'Speculation',
  },

  anatQ: {
    certitude: {
      high: 'high certainty',
      medium: 'medium certainty',
      low: 'low certainty',
      contested: 'contested certainty',
    },
    implication: {
      structural: 'Structural',
      political: 'Political',
      economic: 'Economic',
      social: 'Social',
    },
    essentiel: 'What should you remember?',
    essentielSous: 'What the investigation asserts, what it demonstrates, what follows from it.',
    affirme: 'What is asserted',
    demontre: 'What is demonstrated · {n}',
    decoule: 'What follows',
    enquetes: 'What was investigated?',
    enquetesSous: { one: '{n} sub-investigation: the question asked, what it concludes, the facts and the arguments.', other: '{n} sub-investigations: the question asked, what each concludes, the facts and the arguments.' },
    enquetesReserve: { one: 'This question brings together {n} sub-investigation, with its question, conclusion, facts, arguments and timeline.', other: 'This question brings together {n} sub-investigations, each with its question, conclusion, facts, arguments and timeline.' },
    convergences: 'What keeps coming back?',
    convergencesSous: 'What the sub-investigations establish together, and where they contradict each other.',
    convergencesReserve: { one: 'This question brings together {n} convergence between the sub-investigations, and their contradictions.', other: 'This question brings together {n} convergences between the sub-investigations, and their contradictions.' },
    citoyens: 'What does it cost citizens?',
    citoyensSous: 'The key figure, the orders of magnitude, the direct effects.',
    citoyensReserve: 'This question puts a figure on what the system actually costs: the key figure, what it is equivalent to, the direct effects on households.',
    acteurs: 'Who is involved?',
    acteursSous: 'The people and organisations, from those found everywhere to those in a single sub-investigation.',
    acteursReserve: 'This question brings together the people and organisations of the sub-investigations, sorted by how widely they recur.',
    ouvert: 'What cannot be proven?',
    ouvertSous: 'What the evidence does not allow us to assert, and the question that remains unanswered.',
    questionOuverte: 'The question that remains open',
    sources: 'What is it based on?',
    sourcesSous: { one: 'The documents the sub-investigation relies on.', other: 'The documents the {n} sub-investigations rely on.' },
    sommaire: 'Anatomy contents',
    enNQuestions: 'The anatomy in {n} questions',
  },

  anatConv: {
    force: {
      strong: 'strong convergence',
      moderate: 'moderate convergence',
      weak: 'weak convergence',
    },
    resolution: {
      resolved: 'Resolved',
      partially_resolved: 'Partly resolved',
      non_resolved: 'Unresolved',
    },
    intro: 'A convergence is a mechanism that appears independently in several sub-investigations: a sign that it is systemic, not accidental.',
    titre: 'Convergences · {n}',
    dontFortes: { one: ', {n} of them strong', other: ', {n} of them strong' },
    sousEnquetes: { one: '{n} sub-investigation', other: '{n} sub-investigations' },
    preuvesCles: 'Key evidence · {n}',
    source: 'Source →',
    rechercheComplementaire: 'Further research',
    contradictions: 'Contradictions · {n}',
    dontNonResolues: { one: ', {n} of them unresolved', other: ', {n} of them unresolved' },
    ouEnEst: 'Where the contradiction stands',
    concernees: 'Sub-investigations concerned',
  },

  anatSrc: {
    fiab: {
      official: 'Official document',
      high: 'High reliability',
      medium: 'Medium reliability',
      moderate: 'Moderate reliability',
    },
    court: {
      official: 'Official',
      high: 'High',
      medium: 'Medium',
      moderate: 'Moderate',
    },
    aucune: 'No source has been recorded for this anatomy.',
    documents: { one: 'distinct document', other: 'distinct documents' },
    dont: 'of which',
    solides: { one: 'official or highly reliable', other: 'official or highly reliable' },
    partagees: { one: 'cited by several themes', other: 'cited by several themes' },
    toutes: 'All',
    nonRenseignee: 'Reliability not stated',
    citePar: 'Cited by {n} themes',
  },

  anatCit: {
    echelle: {
      individual: 'Individual',
      household: 'Household',
      community: 'Community',
      societal: 'Societal',
    },
    chiffres: 'Key figures',
    directs: 'Direct impacts',
    indirects: 'Indirect impacts',
    opportunite: 'Opportunity cost',
    geographie: 'By region',
    aRetenir: 'The key figure',
    synthese: 'Summary',
  },

  anatEnt: {
    sousEnquetes: { one: '{n} sub-investigation', other: '{n} sub-investigations' },
    conflit: 'Conflict of interest',
    acteur: 'Person',
    organisation: 'Organisation',
    comptes: '{total} entities listed · {transversales} present in at least two sub-investigations',
    conflits: { one: ' · {n} conflict of interest noted', other: ' · {n} conflicts of interest noted' },
    centraux: 'Present in three or more sub-investigations · {n}',
    autresActeurs: 'Other people · {n}',
    autresOrgs: 'Other organisations · {n}',
  },

  anatPage: {
    certitude: {
      high: 'High certainty',
      medium: 'Medium certainty',
      low: 'Low certainty',
      contested: 'Contested',
      aucune: 'Not assessed',
    },
    anatomies: 'Anatomies',
    metaDescription: 'In-depth investigative files — themes, convergences, actors and conclusions on the subjects that shape the world.',
    kicker: 'Anatomies · major cross-cutting files',
    titre: 'What makes it a system?',
    grandsDossiers: { one: 'major file', other: 'major files' },
    intro: 'An anatomy starts from a subject, opens it up into sub-investigations, then looks for what recurs from one to the next: the same names, the same mechanisms.',
    aucune: 'No anatomy yet',
    aucuneDesc: 'Anatomies will appear as they are published.',
    themes: { one: 'theme', other: 'themes' },
    hautePriorite: ' · {n} high priority',
    convergences: { one: 'convergence', other: 'convergences' },
    erreurReseau: 'Network error',
    chargement: 'Loading the anatomy…',
    introuvable: 'Anatomy not found',
    anatomie: 'Anatomy',
    grandDossier: 'Major file',
    legende: { one: 'sub-investigations · {n} convergence', other: 'sub-investigations · {n} convergences' },
    quiTraverse: 'Who runs through the {n} sub-investigations',
    pied: 'Lumière Noire · Anatomy · {date}',
    metaDefaut: 'In-depth anatomy — {n} themes investigated. Convergences, actors and conclusions by Lumière Noire.',
    ogSous: '{n} themes · Anatomy',
  },

  enqHub: {
    dossiers: {
      nom: 'Files',
      accroche: 'Bring it all together',
      description: 'Cross-cutting investigations linking analyses, investigations and news items around a central subject or entity.',
      voir: 'See the files →',
    },
    anatomies: {
      nom: 'Anatomies',
      accroche: 'Dissect',
      description: 'In-depth investigative files — themes, convergences, actors and conclusions on the subjects that shape the world.',
      voir: 'See the anatomies →',
    },
    loupes: {
      nom: 'Magnifiers',
      accroche: 'Point out the anomaly',
      description: 'Ranked factual anomalies on a news or long-running subject. The Magnifier asks questions; it does not decide.',
      voir: 'See the magnifiers →',
    },
    portee: {
      affair: 'Case',
      period: 'Period',
      event: 'Event',
    },
    bientot: 'First publications coming soon.',
    titreDoc: 'Investigations — Lumière Noire',
    enquetes: 'Investigations',
    kicker: 'Investigations · long-form',
    titre: 'What lies behind it?',
    publiees: { one: 'investigation published', other: 'investigations published' },
    intro: 'Our most thorough formats, where the news cycle has already moved on. Three ways to dig: bring it all together, dissect a system, or point out a specific anomaly.',
  },

  dosListe: {
    ancre: {
      actor: 'Person',
      organization: 'Organisation',
      thematic: 'Thematic',
    },
    metaAnalyse: '✦ META-ANALYSIS',
    premium: '✦ Premium',
    stats: {
      analyses: { one: 'analysis', other: 'analyses' },
      investigations: { one: 'investigation', other: 'investigations' },
      bsScores: { one: 'BS', other: 'BS' },
      recentSignals: { one: 'news item', other: 'news items' },
    },
    tag: 'EDITORIAL INTELLIGENCE',
    titre: 'Files',
    sousTitre: 'Cross-cutting investigations linking analyses, investigations and news items around a central subject or entity.',
    publies: 'Published files',
    avecMeta: 'With meta-analysis',
    contenusLies: 'Linked content',
    type: 'Type',
    tous: 'All',
    compte: { one: '{n} file', other: '{n} files' },
    aucun: 'No file published today',
    aucunType: 'No file of type “{type}”',
    aucunDesc: 'A file is built on analyses already published. We are rebuilding that material before writing new ones.',
  },

  dosFiche: {
    statut: {
      confirmed: 'Confirmed',
      invalidated: 'Invalidated',
      evolving: 'Evolving',
      pending: 'Pending',
    },
    confiance: {
      high: 'Strong',
      medium: 'Moderate',
      low: 'Weak',
    },
    gravite: {
      high: 'Critical',
      medium: 'Moderate',
      low: 'Minor',
    },
    confianceRayon: {
      high: 'Confidence: High',
      medium: 'Confidence: Moderate',
      low: 'Confidence: Low',
    },
    hypotheses: 'Hypotheses',
    contradictions: 'Contradictions',
    noeuds: 'Hidden nodes',
    perspectives: 'Outlook',
    anglesMorts: 'Blind spots',
    metaAnalyse: 'Meta-analysis',
    sources: 'Sources:',
    acteur: 'Person',
    organisation: 'Organisation',
    hypotheseInitiale: 'Initial hypothesis: {texte}',
    investigation: 'Investigation',
    contradictionPrincipale: 'Main contradiction',
    apparaitDans: { one: 'Appears in {n} analysis', other: 'Appears in {n} analyses' },
    investigationComplete: '→ Full investigation',
    coherence: '→ Consistency',
    erreurChargement: 'Loading error',
    chargement: 'Loading the file…',
    introuvable: 'File not found',
    retour: '← Back to files',
    syntheseMeta: '✦ Meta summary',
    introduction: 'Introduction',
    acteursOrgs: 'People & organisations',
    depechesRecentes: 'Recent news items',
    dossiers: 'Files',
    metaDisponible: '✦ Meta-analysis available',
    baseSur: 'Based on',
    analyses: 'Analyses',
    investigations: 'Investigations',
    analysesCoherence: 'Consistency analyses',
    misAJour: 'Updated on {date}',
    gate: 'Full file reserved for Premium subscribers',
    metaPasGeneree: 'The meta-analysis of this file has not been generated yet.',
    rayonsX: 'Linked X-rays',
    analysesAssociees: 'Related analyses',
    aucuneEntite: 'No central entity identified for this file.',
    orgsImpliquees: 'Organisations involved',
    aucuneDepeche: 'No recent news item for this file.',
    filEditorial: 'Editorial thread',
    conclusion: 'Editorial conclusion',
  },

  loupe: {
    gravite: {
      cosmetic: 'Cosmetic',
      serious: 'Serious',
      structural: 'Structural',
    },
    preuve: {
      established: 'Established',
      strongly_indicated: 'Strongly indicated',
      to_verify: 'To be verified',
    },
    singularite: {
      banal: 'Ordinary',
      unusual: 'Unusual',
      highly_unusual: 'Highly unusual',
    },
    registre: {
      procedural: 'Procedural',
      mediatic: 'Media',
      biographic: 'Biographical',
      financial: 'Financial',
      behavioral: 'Behavioural',
      institutional: 'Institutional',
    },
    plausible: 'Plausible',
    moinsPlausible: 'Less plausible',
    evenement: {
      declaration: 'Statement',
      procedural_act: 'Procedural act',
      institutional_event: 'Institutional event',
      media_event: 'Media event',
      biographic_event: 'Biographical event',
      financial_flow: 'Financial flow',
      other: 'Other',
    },
    phase: {
      procedural_act: 'Procedural acts',
      declaration: 'Public statements',
      institutional_event: 'Institutional events',
      media_event: 'Media coverage',
      biographic_event: 'Biographical events',
      financial_flow: 'Financial flows',
      other: 'Miscellaneous events',
    },
    aucunEvenement: 'No dated event has been placed on the timeline.',
    aucunExploitable: 'No usable dated event.',
    chronoIntro: 'The events documented by the file’s anomalies, grouped by year.',
    nEvenements: { one: '{n} event', other: '{n} events' },
    typeActeur: {
      person: 'Person',
      organization: 'Organisation',
      institution: 'Institution',
    },
    aucunActeur: 'No actor identified in this file.',
    acteursIntro: 'The entities identified in several of the file’s anomalies. Pivots bring together their main documented relationships.',
    pivots: 'The file’s pivots',
    mentions: { one: 'mention', other: 'mentions' },
    nMentions: { one: '{n} mention', other: '{n} mentions' },
    autresRelations: { one: 'and {n} other relationship', other: 'and {n} other relationships' },
    autresActeurs: 'Other actors',
  },

  loupeB: {
    refermer: 'Close ↑',
    plus: { one: '{n} source, questions and hypotheses ↓', other: '{n} sources, questions and hypotheses ↓' },
    aucuneAnomalie: 'No anomaly in this file.',
    registre: 'Register',
    tous: 'All',
    tri: 'Sort',
    score: 'Score',
    gravite: 'Severity',
    aucuneFiltre: 'No anomaly for this filter. Click “All” to reset.',
    portee: {
      affair: 'Case',
      period: 'Period',
      event: 'Event',
    },
    loupe: 'Magnifier',
    anomalies: { one: '{n} anomaly', other: '{n} anomalies' },
    structurelles: { one: ' · {n} structural', other: ' · {n} structural' },
    sources: { one: ' · {n} source', other: ' · {n} sources' },
    leDossier: 'The file →',
    onglets: {
      saillantes: 'Salient',
      toutes: 'All',
      acteurs: 'Actors',
      chronologie: 'Timeline',
      nonaffirme: 'Not asserted',
    },
    leDossierAria: 'The file',
    aucuneSaillante: 'No anomaly exceeds the salience threshold (composite score ≥ {seuil}). See the “All” tab for the full list.',
    saillantesIntro: 'The {n} most striking anomalies in the file, out of {total} noted.',
    sourcesN: 'Sources ({n})',
    questionsOuvertes: 'Open questions',
    hypothesesPossibles: 'Possible hypotheses',
    speculatives: 'Speculative hypotheses — not demonstrated',
    nonAffirme: 'Not asserted',
    aucunNonAffirme: 'No mention of an unasserted conclusion in this file.',
    nonAffirmeIntro: 'This file lists factual observations without drawing conclusions. Here are the conclusions a reading might suggest but which this file explicitly refuses to draw.',
  },

  loupeP: {
    metaTitre: 'Magnifier — Lumière Noire',
    metaDescription: 'Ranked factual anomalies on news and long-running subjects. The Magnifier asks questions; it does not decide.',
    enquetes: 'Investigations',
    loupe: 'Magnifier',
    kicker: 'Magnifier · factual anomalies',
    titre: 'What doesn’t add up?',
    legende: { one: 'anomalies noted in {n} file', other: 'anomalies noted in {n} files' },
    intro: 'The documentable friction points of a case — judicial, institutional, procedural — sourced and ranked, with no conclusion on the merits. The Magnifier asks questions; it does not decide.',
    aucune: 'No Magnifier yet',
    aucuneDesc: 'Magnifiers rank factual anomalies; the first ones are coming soon.',
    introuvable: 'File not found — Lumière Noire',
    metaFiche: 'Magnifier file: ranked factual anomalies on “{sujet}”. No conclusion drawn.',
    dossier: 'File',
    legendeFiche: { one: 'anomalies, {n} of them structural', other: 'anomalies, {n} of them structural' },
    sourcesRegistres: { one: '{sources} sources · {n} register', other: '{sources} sources · {n} registers' },
    publieLe: 'published on {date}',
    gate: 'Magnifier file reserved for Premium subscribers',
    construit: { one: 'File built from {sources} web sources, {n} investigative sub-question.', other: 'File built from {sources} web sources, {n} investigative sub-questions.' },
    publieLeMaj: 'Published on {date}.',
  },

  veille: {
    onglets: {
      all: 'All',
      analyses: 'Subjects',
      acteurs: 'Actors',
      scenarios: 'Outlooks',
    },
    importance: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    type: {
      micro_event: 'Minor event',
      major_evolution: 'Major development',
      scenario_update: 'Outlook',
      contradiction: 'Contradiction',
      new_relationship: 'New relationship',
      obsolescence: 'Obsolescence',
    },
    minutes: '{n}min',
    heures: '{n}h',
    jours: '{n}d',
    jour: {
      avenir: 'Upcoming',
      aujourdhui: 'Today',
      hier: 'Yesterday',
      semaine: 'This week',
      mois: 'This month',
      ancien: 'Older',
    },
    critique: 'Critical',
    sources: 'Sources',
    badge: {
      actor: 'Actor',
      scenario: 'Outlook',
      analysis: 'Subject',
    },
    recents: { one: '{n} recent', other: '{n} recent' },
    critiques: { one: '{n} critical', other: '{n} critical' },
    voirFiche: 'See the profile',
    depeches: { one: '{n} news item', other: '{n} news items' },
    titreDoc: 'News — Lumière Noire',
    plusDeSignaux: '{n} more items · {reste} remaining',
    actualite: 'News',
    kickerDate: 'Monitoring · last item on {date}',
    kicker: 'Monitoring',
    titre: 'What has moved?',
    legende: 'news items in 30 days',
    dontCritiques: ' · {n} of them critical',
    depechesOnglet: 'News ({n})',
    entitesOnglet: 'Entities ({n})',
    chargement: 'Loading…',
    aucunResultat: 'No results.',
    aucuneDepeche: 'No news item',
    rienFiltres: 'Nothing matches the active filters.',
    tri: 'Sort',
    triRecents: 'Recent',
    triVelocite: 'Velocity',
    triCritiques: 'Critical',
    rechercher: 'Search a subject…',
    type7: 'Type',
    periode: 'Period',
    j7: '7d',
    j30: '30d',
    tout: 'All',
    critiquesUniquement: 'Critical only',
    entites: { one: '{n} entity', other: '{n} entities' },
    filtreSur: 'Filtered on',
    voirFicheFleche: 'See the profile →',
    deselectionner: 'Deselect',
    depechesTitre: 'News',
    dernieres: 'Latest news',
    aucunePourLeMoment: 'No news item for now',
    aucuneDesc: 'No news matches the active filters. Widen the period or the criticality.',
  },

  veilleCat: {
    legal_action: 'Legal',
    position_change: 'Change of position',
    media_appearance: 'Statement',
    contradiction_with_known: 'Contradiction',
    position_vs_action: 'Words vs deeds',
    unexpected_connection: 'Connection',
    cross_pipeline: 'Cross-check',
    temporal: 'Timing',
    diplomatic: 'Diplomatic',
    new_event: 'Event',
  },

  veilleCarte: {
    maintenant: 'Happening now',
    toutVoir: 'See all →',
    dernieres: 'Latest news.',
    depeche: 'News item',
    voirFiche: 'See the profile →',
    voir: 'See →',
    critique: 'Critical',
    sources: 'Sources',
    replier: 'Collapse →',
    lire: 'Read →',
    statut: {
      confirmed: 'confirmed',
      exploited: 'used',
      detected: 'unverified',
      dismissed: 'rejected',
    },
    confianceFaible: 'low confidence',
    confianceMoy: 'medium confidence',
    voirFicheDe: 'See the {nom} profile →',
  },

  preds: {
    issue: {
      confirmed: 'Confirmed',
      partially_confirmed: 'Partly',
      invalidated: 'Invalidated',
      pending: 'Ongoing',
    },
    retrospective: '✦ Retrospective',
    probaInitiale: 'Initial probability:',
    apercuAdmin: '(admin preview — unpublished analysis)',
    predictionOrigine: 'Original prediction ↗',
    issueConstatee: 'Observed outcome ↗',
    vous: 'You {n}%',
    nous: 'Us {n}%',
    parier: 'Bet — then see our estimate',
    connectez: 'Log in to bet before seeing our estimate →',
    mouvement: 'Notable shift (+{n} pts)',
    echeance: '· Deadline {date}',
    voirAnalyse: 'See the analysis →',
    resolues: 'Resolved',
    enCours: 'Ongoing',
    rechercher: 'Search a scenario…',
    aucunResultat: 'No results',
    aucunResolu: 'No resolved scenario matches this search.',
    aucunEnCours: 'No ongoing scenario matches this search.',
    voirPlus: 'See {n} more',
    voitJuste: 'Do we get it right?',
    calibrationIntro: 'When we put a number on something, does it hold? On scenarios already settled, here is the share that actually happened, by the probability we announced at the outset. Ideally, what we give 80% happens more often than what we give 20%.',
    annonce: 'Announced {tranche}',
    deRealisation: 'came true',
    volumeInsuffisant: 'too few cases',
    pasAssez: 'Not yet enough settled scenarios for a reliable assessment ({n} so far) — come back in a few months.',
    publiees: 'Published predictions',
    confirmees: 'Confirmed',
    partiellement: 'Partly',
    invalidees: 'Invalidated',
    predictions: 'Predictions',
    tag: 'MEMORY · PREDICTIONS',
    titre: 'Our predictions, checked',
    sousTitre: 'We publish dated, quantified scenarios, then show honestly what became of them — hits and misses alike.',
    methodo: 'Each scenario is produced with a dated initial probability, then re-checked periodically by our tracking pipeline: the probability changes as new evidence appears, until a final verdict (confirmed, partly confirmed or invalidated).',
    voirMethodo: 'See the full methodology →',
    aucunePubliee: 'No prediction published yet',
    aucunePublieeDesc: 'Scenarios will appear here as soon as a published analysis contains any.',
  },

  pari: {
    erreurEnregistrement: 'The bet could not be recorded.',
    erreurReseau: 'Network error — please try again.',
    echauffementTitre: 'Warm-up · replay history',
    votrePari: 'Your bet',
    fermer: 'Close',
    horizon: 'Horizon: {texte}',
    echeance: 'Deadline: {date}',
    votreEstimation: 'Your estimate',
    deChances: 'chance that this scenario happens',
    estimationAria: 'Your estimate as a percentage',
    impossible: 'Impossible',
    certain: 'Certain',
    cachee: 'Our estimate stays hidden until you commit — that is what keeps the bet honest.',
    engagerDecouvrir: 'Commit and see the outcome',
    engager: 'Commit my bet',
    vousMieux: 'You saw it more clearly than we did.',
    nousMieux: 'We saw it more clearly this time',
    egalite: 'Neck and neck',
    vous: 'You',
    nous: 'Us',
    cePasse: 'What happened',
    votreScore: 'Your score',
    leNotre: 'Ours',
    explicationScore: 'Your score: the lower it is, the more accurate you were. The warm-up covers scenarios whose outcome is already known — it does not count towards your real record; it is just for practice.',
    enregistre: 'Bet recorded. We will know who was right when the scenario is resolved{echeance}. Your record will update automatically.',
    echeanceParenthese: ' (deadline {date})',
    continuer: 'Continue',
    echauffement: 'Warm-up',
    gammes: 'Practise first',
    gammesIntro: 'Bet blind on scenarios that history has already settled, then find out straight away whether you got it right. Indicative only — it does not count towards your real record, but it is the best way to get your hand in.',
    parierAveugle: 'Bet blind →',
    dejaRejoues: 'Already replayed',
    vousN: 'You {n}%',
    nousN: 'Us {n}%',
    voyezJuste: 'And you — do you get it right?',
    surVosParis: { one: 'On your {n} live bet already settled, here is your score against ours, on exactly the same scenario. The lower it is, the more accurate you were.', other: 'On your {n} live bets already settled, here is your average score against ours, on exactly the same scenarios. The lower it is, the more accurate you were.' },
    battez: 'You beat the model on this set.',
    modeleAvantage: 'The model keeps the edge — for now.',
    enAttente: { one: 'You have {n} live bet awaiting a verdict. Your score will appear here as soon as that scenario is settled.', other: 'You have {n} live bets awaiting a verdict. Your score will appear here as soon as one of those scenarios is settled.' },
    echauffementIndicatif: 'Warm-up (indicative — outcomes already known)',
  },

  login: {
    enAttente: 'Your account is awaiting approval. We will be in touch shortly.',
    desactive: 'This account has been disabled. Please contact the administrator.',
    tropTentatives: 'Too many login attempts. Please try again in about fifteen minutes.',
    incorrect: 'Incorrect email or password.',
    erreurCreation: 'Error while creating the account.',
    erreurReseau: 'Network error.',
    devise: 'Understand so as not to be swept along.',
    promesse: 'Sourced facts, documented contradictions, mapped networks. Your conclusions.',
    parolesTitre: 'Words and deeds',
    parolesTexte: 'commitments set against actions',
    rayonxTexte: 'claims checked, with the evidence',
    reseauxTitre: 'Networks',
    reseauxTexte: 'who sits where, and how we know',
    inscription: 'Sign up',
    connexion: 'Log in',
    demandeEnregistree: 'Request received',
    seraActive: 'Your account will be activated after approval. We will contact you as soon as it is open.',
    retourConnexion: 'Back to log in',
    bonRetour: 'Welcome back.',
    ouvrirCompte: 'Open an account.',
    sousConnexion: 'What you follow, and what has moved since your last visit.',
    sousInscription: 'Accounts are opened after approval: we will contact you.',
    nom: 'Name',
    votreNom: 'Your name',
    email: 'Email',
    emailExemple: 'you@email.com',
    motDePasse: 'Password',
    verification: 'Checking…',
    entrer: 'Enter →',
    demanderCompte: 'Request an account →',
  },

  qg: {
    gratuit: 'Free',
    monQg: 'My HQ',
    titre: 'What you follow, what is moving.',
    elementsSuivis: { one: 'item followed', other: 'items followed' },
    votes: { one: '{n} vote', other: '{n} votes' },
    groupes: { one: '{n} group', other: '{n} groups' },
    intro: 'Your saved analyses, actors and organisations, and what has moved since your last visit. And the next subjects: suggest, vote — the most voted become priorities.',
  },

  agora: {
    retirerVote: 'Remove my vote',
    voter: 'Vote',
    supprimerSuggestion: 'Delete my suggestion',
    confirmerSuppression: 'Delete this suggestion? This cannot be undone.',
    suppressionImpossible: 'Other people have voted for this topic: it can no longer be deleted.',
    pipelineInconnu: 'Unrecognised pipeline.',
    erreurStatut: 'Error {n}',
    pipelineLance: 'Pipeline started successfully.',
    erreurReseau: 'Network error, please try again.',
    prochainsSujets: 'Upcoming subjects',
    proposezVotez: 'Suggest and vote — the most voted go first.',
    proposer: '+ Suggest',
    enCoursLancement: 'Pipeline starting…',
    voirAnalyse: 'See the analysis →',
    affirmationAVerifier: 'Claim to check *',
    titreSujet: 'Subject title *',
    pertinent: 'Why is this subject relevant?',
    region: 'Region',
    domaine: 'Field',
    optGeo: 'Geopolitical analysis',
    optInvestigation: 'Actor investigation',
    optBs: 'Actor consistency',
    optOrg: 'Organisation investigation',
    optRayonx: 'Claim for RayonX',
    annuler: 'Cancel',
    envoi: 'Sending...',
    soumettre: 'Submit',
    lancement: 'Starting…',
    executer: 'Run now',
    aucuneProposition: 'No suggestion yet',
    soyezPremier: 'Be the first to suggest a subject',
    probables: 'Likely next subjects',
    plusVotes: 'most voted',
    mesPropositions: 'My suggestions',
    pasEncore: 'You have not suggested a subject yet.',
    communaute: 'The community suggests',
  },

  portfolio: {
    titre: 'What you follow',
    nouveautes: { one: '{n} new', other: '{n} new' },
    elements: { one: '{n} item', other: '{n} items' },
    tout: 'All',
    groupe: '+ Group',
    renommer: '✎ Rename the group',
    supprimer: '🗑 Delete',
    nomGroupe: 'Group name...',
    creer: 'Create',
    annuler: 'Cancel',
    confirmerGroupe: 'Delete this group? Saved items will stay in your portfolio without a group.',
    supprimerBtn: 'Delete',
    aucun: 'No saved item',
    aucunAide: 'Save analyses, actors and organisations with the 🔖 button on their pages',
    voirVigie: 'See the watch for this item',
    vigie: 'Watch',
    deplacer: 'Move',
    retirer: 'Remove from portfolio',
    deplacerVers: 'Move to',
    sansGroupe: 'No group',
  },

  vigie: {
    nouveau: 'New',
    critique: 'Critical',
    maVigie: 'My watch',
    depeches90: { one: '{n} news item · last 90 days', other: '{n} news items · last 90 days' },
    fermer: 'Close',
    ficheComplete: 'See the full profile →',
    aucune: 'No recent news on this entity.',
    voirPlus: { one: 'See more ({n} left)', other: 'See more ({n} left)' },
  },

  graphe: {
    liens: {
      relationship: 'Relationships',
      membership: 'Memberships',
      co_membership: 'Shared memberships',
      org_link: 'Inter-organisation links',
      investigation: 'Investigations',
      event: 'Events',
    },
    erreurChargement: 'Loading error',
    aucunChemin: 'No path between “{a}” and “{b}” in the loaded graph. Expand other nodes first.',
    coSiege: '{n} people sat at the same time as this subject, in overlapping years. ',
    coCran0: 'The {montrees} strongest links are shown — click to see all {n}.',
    coCran1: 'All {n} are shown — click to go back to the {plafond} strongest.',
    coCran2: 'The {plafond} strongest are shown — click to hide them.',
    coCran3: 'Hidden — click to show them again.',
    connexionsPremium: '{n} investigation links — Premium',
    membresNonAffiches: '+{n} members not shown',
    rechercher: 'Search an entity…',
    depart: 'Click the starting node',
    arrivee: 'From: {nom} → click the destination',
    chemin: 'Path: {noeuds} nodes · {liens} links',
    annuler: '✕ Cancel',
    trouverChemin: '⇢ Find a path',
    personne: 'Person',
    organisation: 'Organisation',
    sansFiche: 'No profile',
    aide: 'Click: details · Double-click: expand · Wheel: zoom · Drag: move',
    construction: 'Building the graph…',
    fermer: 'Close',
    acteur: 'Person',
    entiteSansFiche: 'Entity without a profile',
    clique: ' · clicked in the graph',
    parolesActes: 'Words and deeds · {note}/10',
    investigation: 'Investigation',
    liensDansGraphe: 'Links in the graph',
    ficheComplete: 'See the full profile →',
    deployer: 'Expand the network',
    recentrer: 'Recentre the graph here',
    citeeSansFiche: 'This entity is cited by an investigation but does not yet have a profile in the database.',
    leLien: 'The link · {type}',
    // Text built by /api/graph (13/10/2026).
    api: {
      relation: 'relationship',
      membre: 'member',
      lien: 'link',
      aSiegeAvec: 'sat with',
      cerclesCommuns: { one: '{n} shared circle', other: '{n} shared circles' },
      evenements: { one: '{n} event', other: '{n} events' },
      presence: 'Documented attendance — most recent: {nom}',
      membresPartages: { one: '{n} shared member', other: '{n} shared members' },
      depuis: 'since {annee}',
      enAnnee: 'in {annee}',
      enCours: 'present',
      force: 'strength {n}/10',
    },
    force: 'strength {n}/10',
    etabli: 'What is established',
    selonFiche: ' · according to the {nom} profile',
    pasDescription: 'No detailed description for this link.',
    source: 'Source',
    voirSource: 'See the source',
    socle: {
      organisations: 'Lumière Noire database (organisations pipeline).',
      investigation: 'Lumière Noire database (investigation pipeline).',
      veille: 'Lumière Noire database (monitoring/analysis pipeline).',
    },
    titre: 'The Graph',
    metaDescription: 'Who is linked to whom, and how we know — an interactive exploration of the influence network.',
    tag: 'NETWORK · EXPLORATION',
    sousTitre: 'Who is linked to whom — and above all, how we know. Every link can be clicked and shows its source.',
    aucuneEntite: 'No entity in the database yet.',
    provenance: 'Links come from the Lumière Noire database: relationships documented by monitoring and analyses, memberships of organisations, links between organisations and connections revealed by investigations (subscribers only). Click a link to see its justification and source.',
  },

  indicateur: {
    calcul: 'Calculation',
    seuils: 'Thresholds',
    composantes: 'Components',
    limites: 'Limitations',
    enSavoirPlus: 'Learn more about {nom}',
  },

  rxPoints: {
    exact: 'What is accurate',
    inexact: 'What is inaccurate or missing',
  },

  credits: {
    domainePublic: 'Public domain',
    metaTitre: 'Photo credits — Lumière Noire',
    metaDescription: 'Authors and licences of the portraits and logos shown on Lumière Noire, from Wikimedia Commons and Wikipedia.',
    fichier: 'file ↗',
    licence: 'licence ↗',
    kicker: 'Legal information',
    titre: 'Photo credits',
    intro: 'The site’s {portraits} portraits and {logos} logos come from Wikimedia Commons and Wikipedia. Each is published by its author under the licence shown; portraits may be cropped or shown in two-tone. An omission or an error: the Corrections & right of reply page is there for that.',
    portraits: 'Portraits ({n})',
    logos: 'Logos ({n})',
  },

  page404: {
    titre: 'Page not found',
    erreur: 'Error 404',
    rien: 'The light revealed nothing here.',
    explication: 'This page does not exist, or no longer exists. The link may have changed, or the content has not been published yet. Let us pick up the trail from solid ground.',
    brief: 'Today’s Brief →',
    accueil: 'Home',
    analyses: 'Analyses',
    chercherDesktop: 'Looking for something specific? Use search',
    chercherMobile: 'Looking for something specific? Use the magnifying glass at the top of the page.',
  },

  site: {
    titreDefaut: 'Lumière Noire — Understand so as not to be swept along',
    description: 'Independent geopolitical analysis. Investigations, consistency scores, decoding real power.',
  },

  apropos: {
    titre: 'About',
    metaDescription: 'Why Lumière Noire exists. The story behind the project, its mission, and what sets it apart.',
    ogDescription: 'Between media that repeat and sites that see plots everywhere, something rigorous was missing.',
    devise: 'Understand so as not to be swept along.',
    chapo: 'Between media running on a loop and sites that see plots everywhere, something rigorous was missing. Lumière Noire was born of that frustration.',
    histoireTitre: 'How it all began',
    histoire1: 'Before COVID, I did not follow the news closely. Like many people, I was afraid at the start of the pandemic and followed the authorities’ guidance. It was the logical thing to do.',
    histoire2: 'Then came the Omicron variant. Highly contagious, not very dangerous. I thought: perfect — we will all catch it, we will have immunity, the restrictions will be lifted. That was the obvious public-health logic.',
    histoire3: 'That is when the health pass was introduced. Against all logic. The guidance changed every day, in every direction, without ever admitting the slightest mistake. The scientific councils worked in total opacity. Doctors who voiced different positions were sanctioned or sidelined. And the mainstream media repeated it all on a loop, without any critical thinking.',
    histoire4: 'That moment opened my eyes. Not to push me towards conspiracy theories — quite the opposite. I was looking for something rigorous, sourced, measured. Something that does not take sides but documents, questions, and refuses to bury subjects under the froth of the next news cycle.',
    histoire5: 'I also saw how the powerful were never held to account. How important cases drowned in the endless flow of news. How a subject that “should have changed everything” vanished within a few weeks, with no follow-up and no memory.',
    histoire6: 'Lumière Noire was born of all that.',
    pasTitre: 'What we are not',
    pasMainstream: 'Not a mainstream outlet',
    pasMainstreamTexte: 'We do not repeat official press releases. We do not chase the news hype. We do not run on a loop.',
    pasComplot: 'Not a conspiracy site',
    pasComplotTexte: 'We make no revelations without evidence. We draw no conclusions beyond what the sources allow. We say “we don’t know” when we don’t know.',
    pasPartisan: 'Not a partisan outlet',
    pasPartisanTexte: 'We analyse actions, not camps. A low consistency score is a low consistency score, whether it is the left or the right.',
    pasVerite: 'Not an imposed truth',
    pasVeriteTexte: 'Our job is to give you the evidence. The conclusions are yours to draw.',
    faisonsTitre: 'What we do',
    ecartsGras: 'We measure the gap between words and deeds.',
    ecartsTexte: 'The consistency analysis documents public commitments, sorts them and assesses them one by one, with sources. The result is a score — but above all a list of documented contradictions.',
    memoireGras: 'We keep the record.',
    memoireTexte: 'The news cycle counts on you forgetting. What was said in 2019 and contradicted in 2023 — we find it, document it, connect it. The threads and files are there for that.',
    perspectivesGras: 'We build multi-perspective analyses.',
    perspectivesTexte: 'On complex geopolitical subjects, we do not pick one interpretation and bury the others. We set out competing hypotheses, their arguments, their limits.',
    reseauxGras: 'We map networks.',
    reseauxTexte: 'Who funds what, who knows whom, which organisations influence which decisions — and what it leads to in practice.',
    projetTitre: 'An independent project',
    projet1: 'Lumière Noire is an independent project, with no shareholders, no advertisers and no editorial line dictated by a funder. That means limited resources — and therefore choices: go deeper rather than faster, document rather than comment.',
    projet2: 'The analyses are produced with the help of AI — not only to handle a volume of data no single person could cover, but for a more fundamental reason: to keep the results from being coloured by my feelings or personal convictions. On political subjects it is hard to be fully neutral. AI has no opinion on Macron or Mélenchon. It applies the same protocol to everyone. That is what I was looking for.',
    projet3: 'The same logic guides the voting and subject-suggestion system. If I alone chose the actors and subjects analysed, the platform would inevitably reflect my interests and my blind spots. Letting users suggest and vote ensures that Lumière Noire follows what matters to you — not what matters to me.',
    projet4: 'Every result is checked, every source verified, every score justified. But where we look does not belong to me alone.',
    projet5: 'If this project speaks to you, the best way to support it is to use it, talk about it and, if you wish, take out a subscription that lets us keep it going.',
    explorer: 'Explore the platform',
    explorerTexte: 'Free access to analyses, actor profiles, fact-checks and organisation maps. A free account unlocks My HQ: votes on priority subjects, suggestions, creating collections and bookmarks.',
    accueil: 'Home →',
    methodologie: 'Our methodology',
  },

  couvEdito: {
    pageAccroche: 'Introduction page',
    lire: 'Read the introduction page →',
    replier: '← Collapse',
  },

  qgLabels: {
    type: {
      actor: 'Actor',
      analysis: 'Analysis',
      organization: 'Organisation',
      investigation: 'Investigation',
    },
    pipeline: {
      geo: 'Geopolitics',
      investigation: 'Investigation',
      bs: 'Consistency analysis',
      org: 'Organisation',
      rayonx: 'RayonX',
    },
    statut: {
      pending: 'Pending',
      accepted: 'Accepted',
      in_progress: 'In progress',
      published: 'Published',
      rejected: 'Rejected',
    },
  },

  secteurs: {
    political: 'Politics',
    corporate: 'Business',
    financial: 'Finance',
    media: 'Media',
    academic: 'Academia',
    internationalOrg: 'Intl. organisations',
    military: 'Military',
    autres: 'Other',
  },

  historique: {
    create_position: 'Position added to the profile',
    create_flow: 'Financial flow documented',
  },

  une: {
    titre: 'Lumière Noire – Understand, so as not to be swept along',
    presentation: 'Lumière Noire holds what public officials say up against the facts: promises kept or broken, claims checked, scenarios for ongoing crises, networks of influence. Every piece of information links to its source.',
    pourcent: '{p}%',
    glisser: 'swipe',
    aucunePublication: 'Nothing published yet.',
    contexte: 'Context',
    analyses: {
      titre: 'Analyses',
      texte: 'What is established, what is disputed, the competing perspectives and the likely outcomes.',
      toutes: { one: 'The analysis →', other: 'All {n} analyses →' },
      miseAJour: 'Analysis · updated {date}',
      publiee: 'Analysis · published {date}',
      evolueLe: 'What changed on {date}',
      scenariosChiffres: 'Our quantified scenarios, revised when the facts change.',
      plusProbable: 'The most likely scenario',
      plusProbableDeux: 'Most likely scenario:',
      dIciLe: ', by {date}',
      lesScenarios: 'The scenarios →',
      lire: 'Read the analysis →',
      etabli: 'Established',
      conteste: 'Disputed',
      perspectives: 'The competing perspectives',
      faits: { one: 'established fact', other: 'established facts' },
      nPerspectives: { one: 'perspective', other: 'perspectives' },
      sources: { one: 'source', other: 'sources' },
    },
    promesses: {
      titre: 'Promises kept?',
      texte: 'What they said, what they did.',
      tous: { one: 'The barometer →', other: 'All {n} barometers →' },
      leBarometre: 'The barometer →',
      voirTous: 'See the {n} commitments →',
      voirTousUnParUn: 'See the {n} commitments, one by one →',
      lesN: 'The {n} commitments →',
      misAJour: 'Updated {date}',
      nonTenus: { one: 'not kept', other: 'not kept' },
      partiels: { one: 'partly', other: 'partly' },
      tenus: { one: 'kept', other: 'kept' },
      sansNote: 'unassessed',
      sansExecution: 'not yet assessed',
    },
    bilan: {
      aucunNote: { one: '{n} commitment, none yet assessed against action.', other: '{n} commitments, none yet assessed against action.' },
      nonTenus: { one: '{n} commitment out of {total} not kept', other: '{n} commitments out of {total} not kept' },
      partiels: '{n} partly',
      aucunTenu: 'none kept',
      tenus: { one: '{n} kept', other: '{n} kept' },
      sans: '; {n} not yet assessed',
      separateur: ', ',
      fin: '.',
    },
    depuis: {
      titre: 'Since your last visit on {date}',
      rien: 'Nothing new since: no publication, no revised scenario, no critical dispatch.',
      parus: 'Published since · {n}',
      aEvolue: 'Changed · {sujet}, {date}',
      pourquoi: 'Why it changed →',
      critiques: { one: 'In the monitoring · {n} critical', other: 'In the monitoring · {n} critical' },
      veilleDepuis: 'Monitoring since {date} →',
    },
    actualite: {
      titre: 'The news',
      aVenir: 'Coming up',
      derniersJours: 'Past few days',
      toute: 'All the monitoring →',
    },
    dernieresPublications: 'Latest publications',
    avis: {
      titre: 'What do you think?',
      vrai: 'True',
      faux: 'False',
      detail: 'The details →',
    },
    explorer: {
      titre: 'Explore everything',
      promesses: {
        titre: 'Promises kept?',
        texte: 'What they said, what they did.',
        compte: { one: '{n} barometer', other: '{n} barometers' },
      },
      analyses: {
        titre: 'Analyses and scenarios',
        texte: 'Ongoing crises and what may happen next, quantified and revised.',
        court: 'Ongoing crises and what may happen next.',
        compte: { one: '{n} analysis', other: '{n} analyses' },
      },
      rayonx: {
        titre: 'RayonX',
        texte: 'A claim doing the rounds, checked sentence by sentence.',
        court: 'A claim checked sentence by sentence.',
        compte: { one: '{n} claim', other: '{n} claims' },
        tampon: 'TRUE?',
      },
      reseaux: {
        titre: 'Networks',
        texte: 'Who sits where, who funds whom: dated, sourced links.',
        court: 'Who sits where, who funds whom.',
        compte: '{acteurs} public figures · {orgs} organisations',
      },
      enquetes: {
        titre: 'Investigations',
        texte: 'The cases we dig into beyond the news.',
        court: 'Magnifier, anatomies, investigations.',
        compte: 'Magnifier, anatomies, investigations',
        derniereLoupe: 'Latest Magnifier: {titre}.',
      },
    },
  },

};
